import React, { useCallback, useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import pdfToText from "react-pdftotext";
import {
  Button,
  Drawer,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  message,
} from "antd";
import {
  EditOutlined,
  CalculatorOutlined,
  DownloadOutlined,
  PlusOutlined,
  SaveOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import BankStatementGroupReport from "../../components/BankStatementGroupReport";
import BankStatementAnalysisPanel from "../../components/BankStatementAnalysisPanel";
import SccbPdfViewer from "../../components/SccbPdfViewer";
import "./manage-cases.css";

const API_BASE = "https://www.smart-lend.com/api";
const LOAN_TYPES = ["Unsecured", "Caveat"];
const LOAN_TERMS = ["Bullet", "Int only", "P+I"];
const YES_NO_OPTIONS = ["no", "yes"];
const RENTLY_CASE_STATUSES = [
  "Active",
  "Completed",
  "Pending",
  "Accepted",
  "Rejected",
];
const RENTLY_REPAYMENT_FREQUENCIES = [
  { label: "Monthly repayments", value: "monthly" },
  { label: "Bi-weekly repayments", value: "bi_weekly" },
  { label: "Weekly repayments", value: "weekly" },
];
const RENTLY_INTEREST_BASIS_OPTIONS = [
  { label: "P2P base interest", value: "p2p" },
  { label: "Rolling interest", value: "rolling" },
];
const RENTLY_AMOUNT_MODE_OPTIONS = [
  { label: "Percentage of loan amount", value: "percentage" },
  { label: "Specific value", value: "fixed" },
];
const INSTALLMENT_STATUSES = [
  "Early Repayment",
  "Fully Repaid",
  "Paid",
  "Restructured",
  "T.Month",
];
const FUNDER_PAYMENT_STATUSES = ["not due yet", "repaid"];
const RENTLY_STYLE_MANAGE_CASE_EMAILS = ["lender1@gmail.com"];
const RENTLY_PARSING_STATUSES = new Set([
  "queued",
  "pending",
  "processing",
  "running",
  "started",
  "in_progress",
]);
const isRentlyEmail = (email) => {
  const normalizedEmail = String(email || "")
    .trim()
    .toLowerCase();
  return (
    normalizedEmail.endsWith("@rently.sg") ||
    RENTLY_STYLE_MANAGE_CASE_EMAILS.includes(normalizedEmail)
  );
};

const rentlyDocumentIsParsing = (document) => {
  if (!document) return false;
  const status = String(
    document.status ||
      document.mlcb_ocr_status ||
      document.ocr_status ||
      document.job_status ||
      "",
  )
    .trim()
    .toLowerCase();

  if (status && RENTLY_PARSING_STATUSES.has(status)) {
    return true;
  }

  return Boolean(
    !status &&
    document.bank_document_job_id &&
      document.document_type === "bank_statement" &&
      !document.has_analysis,
  );
};

const rentlyHasParsingDocuments = (profile, report) => {
  const documents = profile?.documents || {};
  const bankStatements = report
    ? Array.isArray(report.submissions)
      ? report.submissions
      : []
    : Array.isArray(documents.bankStatements)
      ? documents.bankStatements
      : [];
  const mlcbReports = report
    ? Array.isArray(report.mlcb_reports)
      ? report.mlcb_reports
      : []
    : Array.isArray(documents.mlcbReport)
      ? documents.mlcbReport
      : [];

  return [...bankStatements, ...mlcbReports].some(rentlyDocumentIsParsing);
};
const CREDIT_FIELD_SECTIONS = [
  {
    title: "Personal Stability",
    fields: [
      {
        key: "age_of_borrower",
        label: "Age of Borrower",
        type: "number",
        min: 0,
      },
      {
        key: "sex",
        label: "Sex",
        type: "select",
        options: ["Male", "Female", "Other"],
        recordOnly: true,
      },
      {
        key: "race",
        label: "Race",
        type: "select",
        options: ["Chinese", "Malay", "Indian", "Others"],
        recordOnly: true,
      },
      {
        key: "marital_status",
        label: "Marital Status",
        type: "select",
        options: ["Married", "Single", "Divorced"],
      },
      {
        key: "housing_type",
        label: "Type of Housing",
        type: "select",
        options: ["Private Property", "HDB Owned", "Renting"],
      },
      {
        key: "bedroom_count",
        label: "Type of accommodation (no. of bedroom)",
        type: "number",
        min: 0,
      },
      {
        key: "property_ownership",
        label: "Ownership of Property",
        type: "select",
        options: ["Yes", "No"],
      },
    ],
  },
  {
    title: "Financial Strength",
    fields: [
      {
        key: "average_personal_noa",
        label: "Average Personal NOA",
        type: "number",
        min: 0,
      },
      {
        key: "bank_statement_quality",
        label: "Bank Statement Quality",
        type: "select",
        options: ["Strong Surplus", "Stable", "Volatile", "Negative"],
      },
      {
        key: "vehicle_tier",
        label: "Vehicle Tier",
        type: "select",
        options: ["Mass Market", "Mid Tier", "Luxury", "Exotic", "None"],
      },
      {
        key: "outstanding_hdb_loan_percentage",
        label: "Outstanding HDB Loan %",
        type: "number",
        min: 0,
        step: "0.01",
        recordOnly: true,
      },
      {
        key: "monthly_loan_instalment_income_percentage",
        label: "Monthly Loan Instalment / Income %",
        type: "number",
        min: 0,
        step: "0.01",
        recordOnly: true,
      },
    ],
  },
  {
    title: "Bureau and Business",
    fields: [
      {
        key: "credit_bureau_rating",
        label: "Credit Bureau Rating",
        type: "text",
      },
      {
        key: "industry_risk",
        label: "Industry Risk",
        type: "select",
        options: ["Low", "Medium", "High"],
      },
      {
        key: "paid_up_capital",
        label: "Paid Up Capital",
        type: "number",
        min: 0,
      },
      {
        key: "business_vintage",
        label: "Business Vintage (years)",
        type: "number",
        min: 0,
        step: "0.01",
      },
      {
        key: "director_shareholder_strength",
        label: "Director / Shareholder Strength",
        type: "select",
        options: ["Asset-backed", "Income-backed", "Weak"],
      },
    ],
  },
  {
    title: "Behavioural SME Risk",
    fields: [
      {
        key: "daily_balance_heatmap",
        label: "Daily Balance Heatmap",
        type: "select",
        options: ["Strong", "Moderate", "Weak"],
      },
      {
        key: "cash_position",
        label: "Cash Position",
        type: "select",
        options: ["High Buffer", "Stable", "Tight"],
      },
      {
        key: "net_cashflow",
        label: "Net Cashflow",
        type: "select",
        options: ["Positive Strong", "Neutral", "Negative"],
      },
      {
        key: "dsr",
        label: "DSR (%)",
        type: "number",
        min: 0,
        max: 100,
        step: "0.01",
      },
      {
        key: "fcc",
        label: "FCC",
        type: "number",
        min: 0,
        step: "0.01",
      },
      {
        key: "commitments",
        label: "Commitments",
        type: "select",
        options: ["Low", "Medium", "High"],
      },
      {
        key: "concentration_risk",
        label: "Concentration Risk",
        type: "select",
        options: ["Low", "Medium", "High"],
      },
      {
        key: "requested_loan_amount",
        label: "Requested Loan Amount",
        type: "number",
        min: 0,
        recordOnly: true,
      },
      {
        key: "requested_tenure_months",
        label: "Requested Tenure (months)",
        type: "number",
        min: 0,
        recordOnly: true,
      },
    ],
  },
];

const emptyCreditInputs = CREDIT_FIELD_SECTIONS.reduce((fields, section) => {
  section.fields.forEach((field) => {
    fields[field.key] = "";
  });
  return fields;
}, {});

const hasCreditValue = (value) =>
  value !== "" && value !== null && value !== undefined;

const selectScore = (value, scores, fallback = 600) => {
  const normalized = textValue(value).trim();
  return Object.prototype.hasOwnProperty.call(scores, normalized)
    ? scores[normalized]
    : fallback;
};

const numericInput = (value) =>
  hasCreditValue(value) ? numberValue(value) : null;

const calculateCreditAssessment = (inputs = {}) => {
  const values = { ...emptyCreditInputs, ...inputs };
  const parameters = [];

  const addParameter = ({
    category,
    parameter,
    weight,
    input,
    score,
    remark,
  }) => {
    const safeScore = Number.isFinite(Number(score)) ? Number(score) : 600;
    parameters.push({
      category,
      parameter,
      weight,
      input: hasCreditValue(input) ? input : "-",
      score: safeScore,
      weighted: roundMoney(weight * safeScore),
      remark,
    });
  };

  const age = numericInput(values.age_of_borrower);
  const averageNoa = numericInput(values.average_personal_noa);
  const paidUpCapital = numericInput(values.paid_up_capital);
  const businessVintage = numericInput(values.business_vintage);
  const bedrooms = numericInput(values.bedroom_count);
  const dsr = numericInput(values.dsr);
  const fcc = numericInput(values.fcc);
  const dsrForScore = dsr === null ? 0 : dsr;
  const fccForScore = fcc === null ? 0 : fcc;
  const bureauRating = textValue(values.credit_bureau_rating)
    .trim()
    .toUpperCase();

  addParameter({
    category: "Behavioural SME Risk",
    parameter: "Daily Balance Heatmap",
    weight: 0.07,
    input: values.daily_balance_heatmap,
    score: selectScore(values.daily_balance_heatmap, {
      Strong: 1000,
      Moderate: 700,
      Weak: 400,
    }),
    remark: "Liquidity pattern from daily balances",
  });
  addParameter({
    category: "Behavioural SME Risk",
    parameter: "Cash Position",
    weight: 0.07,
    input: values.cash_position,
    score: selectScore(values.cash_position, {
      "High Buffer": 1000,
      Stable: 800,
      Tight: 500,
    }),
    remark: "Cash buffer and liquidity strength",
  });
  addParameter({
    category: "Behavioural SME Risk",
    parameter: "Net Cashflow",
    weight: 0.08,
    input: values.net_cashflow,
    score: selectScore(values.net_cashflow, {
      "Positive Strong": 1000,
      Neutral: 700,
      Negative: 300,
    }),
    remark: "Operating cashflow direction",
  });
  addParameter({
    category: "Behavioural SME Risk",
    parameter: "DSR",
    weight: 0.05,
    input: hasCreditValue(values.dsr) ? `${values.dsr}%` : "",
    score:
      dsrForScore <= 30
        ? 1000
        : dsrForScore <= 40
          ? 899
          : dsrForScore <= 50
            ? 749
            : dsrForScore <= 65
              ? 599
              : 399,
    remark: "Debt service affordability",
  });
  addParameter({
    category: "Behavioural SME Risk",
    parameter: "FCC",
    weight: 0.04,
    input: values.fcc,
    score: fccForScore >= 2 ? 1000 : fccForScore >= 1.2 ? 700 : 400,
    remark: "Fixed charge coverage",
  });
  addParameter({
    category: "Behavioural SME Risk",
    parameter: "Commitments",
    weight: 0.03,
    input: values.commitments,
    score: selectScore(values.commitments, {
      Low: 1000,
      Medium: 700,
      High: 400,
    }),
    remark: "Existing repayment and obligation burden",
  });
  addParameter({
    category: "Behavioural SME Risk",
    parameter: "Concentration Risk",
    weight: 0.03,
    input: values.concentration_risk,
    score: selectScore(values.concentration_risk, {
      Low: 1000,
      Medium: 700,
      High: 400,
    }),
    remark: "Customer / revenue concentration",
  });
  addParameter({
    category: "Behavioural SME Risk",
    parameter: "Lifestyle / Vehicle Tier",
    weight: 0.03,
    input: values.vehicle_tier,
    score: selectScore(values.vehicle_tier, {
      "Mass Market": 900,
      "Mid Tier": 800,
      None: 850,
      Luxury: 600,
      Exotic: 400,
    }),
    remark: "Lifestyle stress indicator",
  });
  addParameter({
    category: "Financial Strength",
    parameter: "Average Personal NOA",
    weight: 0.075,
    input: values.average_personal_noa,
    score:
      averageNoa === null
        ? 600
        : averageNoa >= 100000
          ? 1000
          : averageNoa >= 60000
            ? 800
            : averageNoa >= 30000
              ? 600
              : 400,
    remark: "Latest 2-year average NOA",
  });
  addParameter({
    category: "Financial Strength",
    parameter: "Bank Statement Quality",
    weight: 0.075,
    input: values.bank_statement_quality,
    score: selectScore(values.bank_statement_quality, {
      "Strong Surplus": 1000,
      Stable: 800,
      Volatile: 600,
      Negative: 300,
    }),
    remark: "Six-month statement behaviour",
  });
  addParameter({
    category: "Credit Bureau",
    parameter: "Credit Bureau Rating",
    weight: 0.1,
    input: values.credit_bureau_rating,
    score: selectScore(bureauRating, {
      AA: 1000,
      BB: 1000,
      CC: 800,
      DD: 600,
      EE: 300,
      FF: 300,
    }),
    remark: "Bureau repayment behaviour",
  });
  addParameter({
    category: "Business Fundamentals",
    parameter: "Industry Risk",
    weight: 0.05,
    input: values.industry_risk,
    score: selectScore(values.industry_risk, {
      Low: 900,
      Medium: 700,
      High: 500,
    }),
    remark: "SSIC / industry sector risk",
  });
  addParameter({
    category: "Business Fundamentals",
    parameter: "Paid Up Capital",
    weight: 0.05,
    input: values.paid_up_capital,
    score:
      paidUpCapital === null
        ? 600
        : paidUpCapital >= 100000
          ? 1000
          : paidUpCapital >= 50000
            ? 800
            : 600,
    remark: "Capital commitment",
  });
  addParameter({
    category: "Business Fundamentals",
    parameter: "Business Vintage",
    weight: 0.05,
    input: values.business_vintage,
    score:
      businessVintage === null
        ? 600
        : businessVintage >= 5
          ? 1000
          : businessVintage >= 2
            ? 800
            : 500,
    remark: "Years incorporated",
  });
  addParameter({
    category: "Personal Stability",
    parameter: "Age of Borrower",
    weight: 0.025,
    input: values.age_of_borrower,
    score:
      age === null
        ? 600
        : age >= 25 && age <= 55
          ? 1000
          : age >= 21 && age <= 24
            ? 800
            : age >= 56 && age <= 60
              ? 800
              : 500,
    remark: "Borrower age band",
  });
  addParameter({
    category: "Personal Stability",
    parameter: "Marital Status",
    weight: 0.025,
    input: values.marital_status,
    score: selectScore(
      values.marital_status,
      { Married: 1000, Single: 800, Divorced: 600 },
      700,
    ),
    remark: "Personal stability indicator",
  });
  addParameter({
    category: "Personal Stability",
    parameter: "Housing Type",
    weight: 0.025,
    input: values.housing_type,
    score: selectScore(values.housing_type, {
      "Private Property": 1000,
      "HDB Owned": 850,
      Renting: 600,
    }),
    remark: "Housing stability indicator",
  });
  addParameter({
    category: "Personal Stability",
    parameter: "Property / Size",
    weight: 0.025,
    input: `${values.property_ownership || "-"} / ${
      hasCreditValue(values.bedroom_count) ? values.bedroom_count : "-"
    } room`,
    score:
      values.property_ownership === "Yes"
        ? 1000
        : bedrooms === null
          ? 600
          : bedrooms >= 4
            ? 900
            : bedrooms === 3
              ? 750
              : bedrooms <= 2
                ? 600
                : 600,
    remark: "Property ownership and accommodation size",
  });
  addParameter({
    category: "Guarantor Strength",
    parameter: "Director / Shareholder Strength",
    weight: 0.1,
    input: values.director_shareholder_strength,
    score: selectScore(values.director_shareholder_strength, {
      "Asset-backed": 1000,
      "Income-backed": 800,
      Weak: 500,
    }),
    remark: "Director or shareholder backing",
  });

  const finalScore = roundMoney(
    parameters.reduce((total, parameter) => total + parameter.weighted, 0),
  );
  const behaviourWeighted = parameters
    .filter((parameter) => parameter.category === "Behavioural SME Risk")
    .reduce((total, parameter) => total + parameter.weighted, 0);
  const behaviourScore = roundMoney(behaviourWeighted / 0.4);
  const grade =
    finalScore >= 850
      ? "A"
      : finalScore >= 700
        ? "B"
        : finalScore >= 600
          ? "C"
          : "D";
  const baseDecision =
    finalScore >= 850
      ? "Auto Approve"
      : finalScore >= 700
        ? "Approve"
        : finalScore >= 600
          ? "Conditional"
          : "Reject";

  const hardRules = [
    {
      rule: "Negative Cashflow",
      triggered: values.net_cashflow === "Negative",
      action: "Reject",
      owner: "Credit",
    },
    {
      rule: "Weak Daily Balance",
      triggered: values.daily_balance_heatmap === "Weak",
      action: "Reject",
      owner: "Credit",
    },
    {
      rule: "FCC < 1.0x",
      triggered: fccForScore < 1,
      action: "Reject",
      owner: "Credit",
    },
    {
      rule: "DSR > 70%",
      triggered: dsrForScore > 70,
      action: "Reject",
      owner: "Credit",
    },
    {
      rule: "CBS EE/FF",
      triggered: ["EE", "FF"].includes(bureauRating),
      action: "Reject",
      owner: "Credit",
    },
    {
      rule: "Biz < 6 months",
      triggered: businessVintage !== null && businessVintage < 0.5,
      action: "Reject",
      owner: "Credit",
    },
    {
      rule: "Negative Bank Statement",
      triggered: values.bank_statement_quality === "Negative",
      action: "Reject",
      owner: "Credit",
    },
  ];
  const hardRuleBreaches = hardRules.filter((rule) => rule.triggered).length;
  const finalDecision =
    hardRuleBreaches > 0 ? "Reject - Hard Rule Breach" : baseDecision;
  const conditionalFlags = [
    {
      rule: "High industry risk + paid-up capital below 50k",
      triggered:
        values.industry_risk === "High" &&
        paidUpCapital !== null &&
        paidUpCapital < 50000,
    },
    {
      rule: "Luxury/Exotic vehicle + NOA below 60k",
      triggered:
        ["Luxury", "Exotic"].includes(values.vehicle_tier) &&
        averageNoa !== null &&
        averageNoa < 60000,
    },
    {
      rule: "High concentration risk",
      triggered: values.concentration_risk === "High",
    },
    {
      rule: "Weak guarantor",
      triggered: values.director_shareholder_strength === "Weak",
    },
  ];
  const categorySummary = Object.values(
    parameters.reduce((summary, parameter) => {
      const existing = summary[parameter.category] || {
        category: parameter.category,
        weight: 0,
        weighted: 0,
      };
      existing.weight += parameter.weight;
      existing.weighted += parameter.weighted;
      summary[parameter.category] = existing;
      return summary;
    }, {}),
  ).map((item) => ({
    ...item,
    weight: roundMoney(item.weight),
    weighted: roundMoney(item.weighted),
  }));

  return {
    finalScore,
    grade,
    baseDecision,
    finalDecision,
    behaviourScore,
    hardRuleBreaches,
    totalWeight: roundMoney(
      parameters.reduce((total, parameter) => total + parameter.weight, 0),
    ),
    hardRules,
    conditionalFlags,
    categorySummary,
    parameters,
  };
};

const emptyDraft = {
  id: null,
  borrower_name: "",
  borrower_uen: "",
  loan_ref: "",
  credit_grade: "",
  credit_assessment: null,
  approved_amount: "",
  tenor_months: "",
  tenor_days: "",
  loan_type: "",
  loan_terms: "",
  interest_per_month: "",
  processing_fee_percentage: "",
  disbursed_date: "",
  funder_exposures: [],
  installments: [],
  master_rows: [],
  status: "active",
};

const money = (value) =>
  Number(value || 0).toLocaleString("en-SG", {
    style: "currency",
    currency: "SGD",
    maximumFractionDigits: 2,
  });

const rentlyCbsMoney = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const numeric = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(numeric) ? money(numeric) : value;
};

const numberValue = (value) => {
  if (value === "" || value === null || value === undefined) return 0;
  const parsed = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const textValue = (value) =>
  value === null || value === undefined ? "" : String(value);

const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

const yesNoValue = (value) => {
  const normalized = textValue(value).trim().toLowerCase();
  if (normalized === "yes" || normalized === "true" || normalized === "1")
    return "yes";
  if (normalized === "no" || normalized === "false" || normalized === "0")
    return "no";
  return numberValue(value) > 0 ? "yes" : "no";
};

const formatYesNo = (value) => (yesNoValue(value) === "yes" ? "Yes" : "No");

const padDatePart = (value) => String(value).padStart(2, "0");

const dateString = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return [
    date.getFullYear(),
    padDatePart(date.getMonth() + 1),
    padDatePart(date.getDate()),
  ].join("-");
};

const toDateInput = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return dateString(date);
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-SG", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatPercent = (value) => {
  if (value === "" || value === null || value === undefined) return "-";
  return `${numberValue(value)}%`;
};

const formatScore = (value) => {
  if (value === "" || value === null || value === undefined) return "-";
  return Number(value || 0).toFixed(0);
};

const formatTenor = (months, days) => {
  const parts = [];
  if (months !== "" && months !== null && months !== undefined) {
    parts.push(
      `${numberValue(months)} month${numberValue(months) === 1 ? "" : "s"}`,
    );
  }
  if (days !== "" && days !== null && days !== undefined) {
    parts.push(`${numberValue(days)} day${numberValue(days) === 1 ? "" : "s"}`);
  }
  return parts.length ? parts.join(" ") : "-";
};

const formatFunderExposures = (exposures = [], obligations = []) => {
  if (!Array.isArray(exposures) || exposures.length === 0) return [];

  return exposures.map((exposure) => {
    const obligation = obligations.find(
      (item) => String(item.funder_id) === String(exposure.funder_id),
    );
    return {
      name:
        exposure.funder_name ||
        obligation?.funder_name ||
        `Funder ${exposure.funder_id}`,
      amount: exposure.exposure_amount,
    };
  });
};

const normalizeStatementGroupValue = (value, fallback) => {
  if (value === null || value === undefined) return fallback;
  const normalized = String(value).trim();
  return normalized === "" ? fallback : normalized;
};

const getStatementMonthTimestamp = (statement) => {
  const rawValue = normalizeStatementGroupValue(
    statement?.statement_month_year,
    "",
  );

  if (!rawValue) return null;

  const normalizedValue = rawValue.replace(/-/g, "/").trim();
  const mmYyyyMatch = normalizedValue.match(/^(\d{1,2})\/(\d{4})$/);
  if (mmYyyyMatch) {
    const month = Number(mmYyyyMatch[1]) - 1;
    const year = Number(mmYyyyMatch[2]);
    if (month >= 0 && month <= 11) return new Date(year, month, 1).getTime();
  }

  const yyyyMmMatch = normalizedValue.match(/^(\d{4})\/(\d{1,2})$/);
  if (yyyyMmMatch) {
    const year = Number(yyyyMmMatch[1]);
    const month = Number(yyyyMmMatch[2]) - 1;
    if (month >= 0 && month <= 11) return new Date(year, month, 1).getTime();
  }

  const parsedDate = new Date(`01 ${rawValue}`);
  if (!Number.isNaN(parsedDate.getTime())) {
    return new Date(
      parsedDate.getFullYear(),
      parsedDate.getMonth(),
      1,
    ).getTime();
  }

  return null;
};

const compareBankStatementsByMonth = (left, right) => {
  const leftTimestamp = getStatementMonthTimestamp(left);
  const rightTimestamp = getStatementMonthTimestamp(right);

  if (leftTimestamp !== null && rightTimestamp !== null) {
    return leftTimestamp - rightTimestamp;
  }

  if (leftTimestamp !== null) return -1;
  if (rightTimestamp !== null) return 1;

  return normalizeStatementGroupValue(
    left?.statement_month_year,
    "",
  ).localeCompare(
    normalizeStatementGroupValue(right?.statement_month_year, ""),
  );
};

const groupAnalysisStatements = (statements = []) => {
  const groupedStatements = new Map();

  statements.forEach((statement) => {
    const bank = normalizeStatementGroupValue(statement?.bank, "Unknown Bank");
    const accountNumber = normalizeStatementGroupValue(
      statement?.bank_account_number,
      "-",
    );
    const groupKey = `${bank.toLowerCase()}::${accountNumber.toLowerCase()}`;

    if (!groupedStatements.has(groupKey)) {
      groupedStatements.set(groupKey, {
        groupKey,
        bank,
        accountNumber,
        statements: [],
      });
    }

    groupedStatements.get(groupKey).statements.push(statement);
  });

  return Array.from(groupedStatements.values())
    .map((group) => ({
      ...group,
      statements: [...group.statements].sort(compareBankStatementsByMonth),
    }))
    .sort((left, right) => {
      const bankDiff = left.bank.localeCompare(right.bank);
      if (bankDiff !== 0) return bankDiff;
      return left.accountNumber.localeCompare(right.accountNumber);
    });
};

const getCbsObligationRows = (cbsReports = []) =>
  cbsReports.flatMap((report) =>
    (report.parsed_payload?.cbs_obligations || []).map((obligation, index) => ({
      ...obligation,
      id: `${report.id || "cbs"}-${index}`,
      report_name: report.original_file_name || report.name,
      enquiry_date: report.parsed_payload?.enquiry_date,
    })),
  );

const getCbsMonthlyInstalmentHistories = (cbsReports = []) =>
  cbsReports
    .map((report) => ({
      id: report.id || report.original_file_name || report.name,
      report_name: report.original_file_name || report.name,
      enquiry_date: report.parsed_payload?.enquiry_date,
      rows: report.parsed_payload?.cbs_monthly_instalment_history || [],
    }))
    .filter((history) => history.rows.length > 0);

const extractTextBetween = (text, beforeText, afterText) => {
  if (!text) return "";
  const beforeTextPattern = cbsLabelPattern(beforeText);
  const afterTextPattern = cbsLabelPattern(afterText);
  const regex = new RegExp(`${beforeTextPattern}(.*?)${afterTextPattern}`, "i");
  const match = text.match(regex);
  return match && match[1] ? match[1].trim() : "";
};

const escapeRegex = (value) =>
  String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const cbsLabelPattern = (value) => escapeRegex(value).replace(/\s+/g, "\\s+");

const normalizeCbsAmount = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const numeric = Number(
    String(value)
      .replace(/^\((.*)\)$/, "-$1")
      .replace(/[^0-9.-]/g, ""),
  );
  return Number.isFinite(numeric) ? numeric : null;
};

const extractCbsEnquiryDate = (text) => {
  const match = String(text || "").match(
    /enquiry\s+date\s*[:\s]+([A-Za-z]{3,9}\s+\d{1,2},?\s+\d{4}|\d{1,2}\s+[A-Za-z]{3,9}\s+\d{4}|\d{2}\/\d{2}\/\d{4})/i,
  );
  return match?.[1]?.trim() || null;
};

const extractCbsSection = (text, startLabel, endLabels = []) => {
  if (!text) return "";
  const startIndex = text.search(new RegExp(cbsLabelPattern(startLabel), "i"));
  if (startIndex < 0) return "";

  const remainder = text.slice(startIndex);
  const endIndexes = endLabels
    .map((label) => {
      const matchIndex = remainder
        .slice(1)
        .search(new RegExp(cbsLabelPattern(label), "i"));
      return matchIndex >= 0 ? matchIndex + 1 : -1;
    })
    .filter((index) => index > 0);

  return remainder.slice(
    0,
    endIndexes.length ? Math.min(...endIndexes) : undefined,
  );
};

const extractCbsLatestTotals = (text, startLabel, endLabels = []) => {
  const section = extractCbsSection(text, startLabel, endLabels);
  const normalized = String(section || "")
    .replace(/\s+/g, " ")
    .trim();
  const totalMatch = normalized.match(
    /\bTotal\s+([-+]?\.?\d[\d,]*(?:\.\d+)?|NA)\s+([-+]?\.?\d[\d,]*(?:\.\d+)?|NA)\s+([-+]?\.?\d[\d,]*(?:\.\d+)?|NA)\s+([-+]?\.?\d[\d,]*(?:\.\d+)?|NA)/i,
  );

  if (!totalMatch) return [];

  return totalMatch
    .slice(1, 5)
    .map((value) => (/^NA$/i.test(value) ? null : normalizeCbsAmount(value)));
};

const sumCbsTrailingAmounts = (amounts) =>
  amounts.reduce((sum, amount) => {
    const numeric = normalizeCbsAmount(amount);
    return sum + (Number.isFinite(numeric) ? numeric : 0);
  }, 0);

const parseCbsMonthlyInstalmentHistory = (text) => {
  const section = extractCbsSection(text, "Aggregated Monthly Instalment", [
    "Disclaimer",
    "End Of Report",
  ]);
  const normalized = String(section || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!normalized) return [];

  const rows = [];
  const latestMonth = normalized.match(/\b([A-Za-z]+ \d{4})\*?\b/)?.[1] || null;
  const totalMatch = normalized.match(
    /\bTotal\s+([-+]?\.?\d[\d,]*(?:\.\d+)?)\s+([-+]?\.?\d[\d,]*(?:\.\d+)?)\s+([-+]?\.?\d[\d,]*(?:\.\d+)?)\s+([-+]?\.?\d[\d,]*(?:\.\d+)?)/i,
  );

  if (latestMonth && totalMatch) {
    rows.push({
      month_label: latestMonth,
      monthly_instalment: sumCbsTrailingAmounts(totalMatch.slice(1, 5)),
    });
  }

  const precedingSection = extractCbsSection(
    section,
    "Aggregated Monthly Instalments for Preceding 5 Months",
    ["* The", "Disclaimer", "End Of Report"],
  ).replace(/^Aggregated Monthly Instalments for Preceding 5 Months/i, "");
  const precedingNormalized = precedingSection.replace(/\s+/g, " ").trim();
  const precedingRegex =
    /\b([A-Za-z]+ \d{4})\s+All\s+All(?:\s+All|\s+-)?\s+([-+]?\.?\d[\d,]*(?:\.\d+)?|NA)\s+([-+]?\.?\d[\d,]*(?:\.\d+)?|NA)\s+([-+]?\.?\d[\d,]*(?:\.\d+)?|NA)\s+([-+]?\.?\d[\d,]*(?:\.\d+)?|NA)/gi;
  let match;

  while ((match = precedingRegex.exec(precedingNormalized)) !== null) {
    rows.push({
      month_label: match[1],
      monthly_instalment: sumCbsTrailingAmounts(
        match.slice(2, 6).filter((value) => !/^NA$/i.test(value)),
      ),
    });
  }

  return rows.slice(0, 6).reverse();
};

const parseCbsOutstandingHistory = (text) => {
  const section = extractCbsSection(
    text,
    "Aggregated Outstanding Balances for Preceding 5 Months",
    ["Aggregated Monthly Instalment", "Disclaimer", "End Of Report"],
  );
  const normalized = String(section || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!normalized) return [];

  const rows = [];
  const precedingRegex =
    /\b([A-Za-z]+ \d{4})\s+All\s+All(?:\s+All|\s+-)?\s+([-+]?\.?\d[\d,]*(?:\.\d+)?|NA)\s+([-+]?\.?\d[\d,]*(?:\.\d+)?|NA)\s+([-+]?\.?\d[\d,]*(?:\.\d+)?|NA)\s+([-+]?\.?\d[\d,]*(?:\.\d+)?|NA)/gi;
  let match;

  while ((match = precedingRegex.exec(normalized)) !== null) {
    rows.push({
      date: match[1],
      value: String(
        sumCbsTrailingAmounts(
          match.slice(2, 6).filter((value) => !/^NA$/i.test(value)),
        ),
      ),
    });
  }

  return rows.slice(0, 5).reverse();
};

const formatCbsCurrencyValue = (value) =>
  value === null || value === undefined || Number.isNaN(Number(value))
    ? null
    : Number(value).toLocaleString("en-US", {
        style: "currency",
        currency: "SGD",
      });

const formatCbsPercentValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return numeric.toFixed(2);
};

const normalizeCbsRiskGrade = (value) => {
  const match = String(value || "")
    .trim()
    .match(/^([A-H]{2})/i);
  return match ? match[1].toUpperCase() : String(value || "").trim();
};

const parseRentlyCbsData = (text) => {
  if (!text) return null;

  const securedCreditLimit = extractTextBetween(
    text,
    "Secured Credit Limit",
    "Unsecured Credit Limit",
  );
  const unsecuredCreditLimit = extractTextBetween(
    text,
    "Unsecured Credit Limit",
    "Exempted Credit Limit",
  );
  const creditScore = extractTextBetween(
    text,
    "financial institution.  Score",
    "Risk Grade",
  );
  const riskGrade = extractTextBetween(
    text,
    "Risk Grade ",
    "Risk Grade Description",
  );
  const probabilityDefault = extractTextBetween(
    text,
    "Probability of Default ",
    " For more details on the description",
  );
  const outstandingTotals = extractCbsLatestTotals(
    text,
    "Aggregated Outstanding Balances",
    [
      "Aggregated Outstanding Balances for Preceding 5 Months",
      "Aggregated Monthly Instalment",
    ],
  );
  const instalmentTotals = extractCbsLatestTotals(
    text,
    "Aggregated Monthly Instalment",
    [
      "Aggregated Monthly Instalments for Preceding 5 Months",
      "Disclaimer",
      "End Of Report",
    ],
  );
  const securedLimit = normalizeCbsAmount(securedCreditLimit);
  const unsecuredLimit = normalizeCbsAmount(unsecuredCreditLimit);
  const securedUtilized = outstandingTotals[0] ?? null;
  const unsecuredUtilized =
    (outstandingTotals[1] ?? 0) + (outstandingTotals[2] ?? 0) || null;
  const monthlyCommitment =
    (instalmentTotals[0] ?? 0) + (instalmentTotals[1] ?? 0) || null;
  const outstandingHistory = parseCbsOutstandingHistory(text);
  const cbsMonthlyInstalments = parseCbsMonthlyInstalmentHistory(text);
  const normalizedRiskGrade = normalizeCbsRiskGrade(riskGrade);
  const cbsObligations =
    securedUtilized === null &&
    unsecuredUtilized === null &&
    monthlyCommitment === null
      ? []
      : [
          {
            source: "CBS",
            facility: "Aggregated CBS facilities",
            outstanding_balance:
              (securedUtilized ?? 0) + (unsecuredUtilized ?? 0) || null,
            monthly_instalment: monthlyCommitment,
          },
        ];

  const payload = {
    secured_credit_limit: securedCreditLimit || null,
    unsecured_credit_limit: unsecuredCreditLimit || null,
    credit_score: creditScore || null,
    risk_grade: normalizedRiskGrade || null,
    probability_default: probabilityDefault || null,
    enquiry_date: extractCbsEnquiryDate(text),
    cbs_obligations: cbsObligations,
    cbs_monthly_instalment_history: cbsMonthlyInstalments,
    EnquiryDate: extractCbsEnquiryDate(text),
    SecuredCreditLimit: securedLimit,
    UnsecuredCreditLimit: unsecuredLimit,
    CreditScore: normalizeCbsAmount(creditScore),
    RiskGrade: normalizedRiskGrade || null,
    ProbabilityofDefault: normalizeCbsAmount(probabilityDefault),
    SecuredCreditUtilized: securedUtilized,
    UnsecuredCreditUtilized: unsecuredUtilized,
    SecuredCreditUtilizationinpercentage: securedLimit
      ? formatCbsPercentValue((securedUtilized / securedLimit) * 100)
      : null,
    UnsecuredCreditUtilizationinpercentage: unsecuredLimit
      ? formatCbsPercentValue((unsecuredUtilized / unsecuredLimit) * 100)
      : null,
    MonthlyCommitment: formatCbsCurrencyValue(monthlyCommitment),
    Last6MonthsGraphChartMaxData: outstandingHistory.length
      ? Math.max(...outstandingHistory.map((row) => Number(row.value) || 0))
      : null,
    Last6MonthsGraphChartData: outstandingHistory.length
      ? outstandingHistory
      : null,
    defaults: normalizeCbsAmount(
      extractTextBetween(text, "Defaults", "Bankruptcy Proceedings"),
    ),
    bankcruptcyProceedings: normalizeCbsAmount(
      extractTextBetween(
        text,
        "Bankruptcy Proceedings",
        "Secured Credit Limit",
      ),
    ),
    overdueBalance: normalizeCbsAmount(
      extractTextBetween(text, "Overdue Balance", "Defaults"),
    ),
  };

  return Object.values(payload).some((value) =>
    Array.isArray(value) ? value.length > 0 : Boolean(value),
  )
    ? payload
    : null;
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const addMonths = (date, months) => {
  const next = new Date(date);
  const day = next.getDate();
  next.setMonth(next.getMonth() + months);
  if (next.getDate() < day) {
    next.setDate(0);
  }
  return next;
};

const closestWeekday = (date) => {
  const day = date.getDay();
  if (day === 6) return addDays(date, -1);
  if (day === 0) return addDays(date, 1);
  return date;
};

const closestWeekdayInputDate = (value) => {
  if (!value) return "";
  return dateString(closestWeekday(new Date(`${value}T00:00:00`)));
};

const normaliseMasterRow = (row) => ({
  ...row,
  principal_repaid_to_funder: yesNoValue(row?.principal_repaid_to_funder),
  loan_closed: yesNoValue(row?.loan_closed),
});

const normaliseCase = (item) => ({
  ...emptyDraft,
  ...item,
  borrower_name: textValue(item?.borrower_name),
  borrower_uen: textValue(item?.borrower_uen),
  loan_ref: textValue(item?.loan_ref),
  credit_grade: textValue(item?.credit_grade),
  credit_assessment: item?.credit_assessment || null,
  loan_type: textValue(item?.loan_type),
  loan_terms: textValue(item?.loan_terms),
  approved_amount: item?.approved_amount ?? "",
  tenor_months: item?.tenor_months ?? "",
  tenor_days: item?.tenor_days ?? "",
  interest_per_month: item?.interest_per_month ?? "",
  processing_fee_percentage: item?.processing_fee_percentage ?? "",
  disbursed_date: toDateInput(item?.disbursed_date),
  funder_exposures: Array.isArray(item?.funder_exposures)
    ? item.funder_exposures
    : [],
  installments: Array.isArray(item?.installments) ? item.installments : [],
  master_rows: Array.isArray(item?.master_rows)
    ? item.master_rows.map(normaliseMasterRow)
    : [],
});

const buildQuarterlyRepaymentDates = (installments) => {
  const dates = {};
  installments.forEach((row, index) => {
    const quarterEndIndex = Math.min(
      Math.ceil((index + 1) / 3) * 3,
      installments.length,
    );
    const quarterEndRow = installments[quarterEndIndex - 1];
    dates[index + 1] = quarterEndRow?.due_date
      ? dateString(addDays(new Date(`${quarterEndRow.due_date}T00:00:00`), 7))
      : "";
  });
  return dates;
};

const generateSchedules = (draft, funders) => {
  const principal = numberValue(draft.approved_amount);
  const months = Math.max(0, Math.floor(numberValue(draft.tenor_months)));
  const days = Math.max(0, Math.floor(numberValue(draft.tenor_days)));
  const interestRate = numberValue(draft.interest_per_month) / 100;
  const feeRate = numberValue(draft.processing_fee_percentage) / 100;
  const terms = draft.loan_terms;
  const principalPerMonth = months > 0 ? principal / months : 0;
  const interestPerMonth = principal * interestRate;
  const feeAmount = principal * feeRate;
  const selectedExposures = Array.isArray(draft.funder_exposures)
    ? draft.funder_exposures
    : [];

  const installments = Array.from({ length: months }).map((_, idx) => {
    const period = idx + 1;
    let dueDate = "";

    if (draft.disbursed_date) {
      const baseDate = new Date(`${draft.disbursed_date}T00:00:00`);
      dueDate = dateString(
        closestWeekday(addDays(addMonths(baseDate, period), days)),
      );
    }

    let principalCollected = 0;
    let interestCollected = 0;
    let installmentAmount = "";

    if (terms === "Int only") {
      interestCollected = interestPerMonth;
      principalCollected = period === months ? principal : 0;
      installmentAmount = interestCollected + principalCollected;
    } else if (terms === "P+I") {
      interestCollected = interestPerMonth;
      principalCollected = principalPerMonth;
      installmentAmount = interestCollected + principalCollected;
    } else if (terms === "Bullet") {
      installmentAmount = "";
      interestCollected = 0;
      principalCollected = 0;
    }

    const principalPaidThroughRow =
      terms === "P+I"
        ? principalPerMonth * period
        : terms === "Int only" && period === months
          ? principal
          : 0;
    const balance =
      terms === "P+I"
        ? Math.max(principal - principalPaidThroughRow, 0)
        : terms === "Int only" && period === months
          ? 0
          : principal;

    return {
      period,
      due_date: dueDate,
      amount: installmentAmount === "" ? "" : roundMoney(installmentAmount),
      principal_collected: roundMoney(principalCollected),
      interest_collected: roundMoney(interestCollected),
      fee_collected: period === 1 ? roundMoney(feeAmount) : 0,
      loan_balance: roundMoney(balance),
    };
  });

  const repaymentDates = buildQuarterlyRepaymentDates(installments);

  const masterRows = installments.map((row) => {
    const funderObligations = selectedExposures.map((exposure) => {
      const funder = funders.find(
        (item) => String(item.id) === String(exposure.funder_id),
      );
      const exposureAmount = numberValue(exposure.exposure_amount);
      const costRate = numberValue(funder?.cost_of_fund_rate) / 100;

      return {
        funder_id: exposure.funder_id,
        funder_name: funder?.name || exposure.funder_name || "Funder",
        exposure_amount: roundMoney(exposureAmount),
        cost_of_fund_rate: numberValue(funder?.cost_of_fund_rate),
        interest_due_to_funder: roundMoney(exposureAmount * costRate),
        principal_due_to_funder:
          row.period === months ? roundMoney(exposureAmount) : 0,
        funder_repayment_date: repaymentDates[row.period] || "",
        funder_payment_status:
          row.period === months ? "not due yet" : "not due yet",
      };
    });

    const totalCostOfFund = funderObligations.reduce(
      (total, funder) => total + numberValue(funder.interest_due_to_funder),
      0,
    );
    const totalPrincipalDue = funderObligations.reduce(
      (total, funder) => total + numberValue(funder.principal_due_to_funder),
      0,
    );
    const bfcRevenue =
      numberValue(row.interest_collected) +
      numberValue(row.fee_collected) -
      totalCostOfFund;

    return {
      ...row,
      installment_status: "",
      installment_paid_date: "",
      loan_end_date: installments[installments.length - 1]?.due_date || "",
      funder_obligations: funderObligations,
      bfc_revenue: roundMoney(bfcRevenue),
      principal_repaid: 0,
      principal_still_owing: roundMoney(Math.max(totalPrincipalDue, 0)),
      principal_repaid_to_funder: "no",
      loan_closed: "no",
    };
  });

  return { installments, masterRows };
};

const SmartLendManageCases = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [funders, setFunders] = useState([]);
  const [cases, setCases] = useState([]);
  const [draft, setDraft] = useState(emptyDraft);
  const [mode, setMode] = useState("master");
  const [funderModalOpen, setFunderModalOpen] = useState(false);
  const [editingFunder, setEditingFunder] = useState(null);
  const [funderForm, setFunderForm] = useState({
    name: "",
    cost_of_fund_rate: "",
  });
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [creditInputs, setCreditInputs] = useState(emptyCreditInputs);

  const token = Cookies.get("token");

  const authHeaders = useMemo(
    () => ({
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    }),
    [token],
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/lender/manage-cases`, {
        method: "GET",
        headers: authHeaders,
      });
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json.message || "Unable to load manage cases.");
      }
      setFunders(json.funders || []);
      setCases((json.cases || []).map(normaliseCase));
    } catch (error) {
      message.error(error.message || "Unable to load manage cases.");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const borrowerSuggestions = useMemo(() => {
    const query = textValue(draft.borrower_name).trim().toLowerCase();
    if (!query) return [];
    const seen = new Set();
    return cases
      .filter((item) =>
        textValue(item.borrower_name).toLowerCase().includes(query),
      )
      .filter((item) => {
        const key = `${item.borrower_name}|${item.borrower_uen}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 5);
  }, [cases, draft.borrower_name]);

  const loanRefSuggestions = useMemo(() => {
    const query = textValue(draft.loan_ref).trim().toLowerCase();
    if (!query) return [];
    return cases
      .map((item) => item.loan_ref)
      .filter(Boolean)
      .filter((loanRef, index, all) => all.indexOf(loanRef) === index)
      .filter((loanRef) => textValue(loanRef).toLowerCase().includes(query))
      .slice(0, 5);
  }, [cases, draft.loan_ref]);

  const selectedFunderIds = useMemo(
    () => draft.funder_exposures.map((item) => String(item.funder_id)),
    [draft.funder_exposures],
  );

  const selectedFunders = useMemo(
    () => funders.filter((item) => selectedFunderIds.includes(String(item.id))),
    [funders, selectedFunderIds],
  );

  const funderUnrepaidTotals = useMemo(() => {
    const totals = new Map();

    cases.forEach((caseItem) => {
      const rows = Array.isArray(caseItem.master_rows)
        ? caseItem.master_rows
        : [];
      const exposures = Array.isArray(caseItem.funder_exposures)
        ? caseItem.funder_exposures
        : [];
      const isPrincipalRepaid =
        rows.length > 0 &&
        rows.every(
          (row) => yesNoValue(row.principal_repaid_to_funder) === "yes",
        );

      if (isPrincipalRepaid) return;

      exposures.forEach((exposure) => {
        const funderId = String(exposure.funder_id);
        totals.set(
          funderId,
          numberValue(totals.get(funderId)) +
            numberValue(exposure.exposure_amount),
        );
      });
    });

    return totals;
  }, [cases]);

  const creditResult = useMemo(
    () => calculateCreditAssessment(creditInputs),
    [creditInputs],
  );

  const flatMasterRows = useMemo(() => {
    return cases.flatMap((caseItem) =>
      (caseItem.master_rows || []).map((row) => ({
        ...row,
        case_id: caseItem.id,
        borrower_name: caseItem.borrower_name,
        borrower_uen: caseItem.borrower_uen,
        loan_ref: caseItem.loan_ref,
        credit_grade: caseItem.credit_grade,
        approved_amount: caseItem.approved_amount,
        funder_exposures: caseItem.funder_exposures,
        tenor_months: caseItem.tenor_months,
        tenor_days: caseItem.tenor_days,
        loan_type: caseItem.loan_type,
        loan_terms: caseItem.loan_terms,
        interest_per_month: caseItem.interest_per_month,
        processing_fee_percentage: caseItem.processing_fee_percentage,
        disbursed_date: caseItem.disbursed_date,
      })),
    );
  }, [cases]);

  const updateDraft = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const openCreditModal = () => {
    setCreditInputs({
      ...emptyCreditInputs,
      ...(draft.credit_assessment?.inputs || {}),
    });
    setCreditModalOpen(true);
  };

  const applyCreditAssessment = () => {
    setDraft((current) => ({
      ...current,
      credit_grade: creditResult.grade,
      credit_assessment: {
        inputs: creditInputs,
        result: creditResult,
      },
    }));
    setCreditModalOpen(false);
  };

  const setSelectedFunders = (ids) => {
    setDraft((current) => {
      const nextExposures = ids.map((id) => {
        const existing = current.funder_exposures.find(
          (item) => String(item.funder_id) === String(id),
        );
        const funder = funders.find((item) => String(item.id) === String(id));
        return (
          existing || {
            funder_id: id,
            funder_name: funder?.name || "",
            exposure_amount: "",
          }
        );
      });

      return { ...current, funder_exposures: nextExposures };
    });
  };

  const updateExposure = (funderId, value) => {
    setDraft((current) => ({
      ...current,
      funder_exposures: current.funder_exposures.map((item) =>
        String(item.funder_id) === String(funderId)
          ? { ...item, exposure_amount: value }
          : item,
      ),
    }));
  };

  const updateMasterRow = (index, field, value) => {
    const nextValue =
      field === "due_date" ? closestWeekdayInputDate(value) : value;
    setDraft((current) => ({
      ...current,
      master_rows: current.master_rows.map((row, rowIndex) => {
        const shouldUpdateLoanEndDate =
          field === "due_date" && index === current.master_rows.length - 1;

        if (field === "loan_closed" || field === "principal_repaid_to_funder") {
          return { ...row, [field]: nextValue };
        }

        if (rowIndex !== index) {
          return shouldUpdateLoanEndDate
            ? { ...row, loan_end_date: nextValue }
            : row;
        }

        return {
          ...row,
          [field]: nextValue,
          loan_end_date: shouldUpdateLoanEndDate
            ? nextValue
            : row.loan_end_date,
        };
      }),
      installments: current.installments.map((row, rowIndex) =>
        rowIndex === index && ["due_date", "amount"].includes(field)
          ? { ...row, [field]: nextValue }
          : row,
      ),
    }));
  };

  const updateFunderObligation = (rowIndex, funderIndex, field, value) => {
    setDraft((current) => ({
      ...current,
      master_rows: current.master_rows.map((row, currentRowIndex) => {
        if (currentRowIndex !== rowIndex) return row;
        return {
          ...row,
          funder_obligations: (row.funder_obligations || []).map(
            (funder, currentFunderIndex) =>
              currentFunderIndex === funderIndex
                ? { ...funder, [field]: value }
                : funder,
          ),
        };
      }),
    }));
  };

  const recalculateSchedules = () => {
    const { installments, masterRows } = generateSchedules(draft, funders);
    setDraft((current) => ({
      ...current,
      installments,
      master_rows: masterRows,
    }));
    setMode("form");
  };

  const startNewCase = () => {
    setDraft(emptyDraft);
    setMode("form");
  };

  const editCase = (caseItem) => {
    setDraft(normaliseCase(caseItem));
    setMode("form");
  };

  const saveCase = async () => {
    if (!textValue(draft.borrower_name).trim()) {
      message.error("Borrower name is required.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...draft,
        approved_amount:
          draft.approved_amount === ""
            ? null
            : numberValue(draft.approved_amount),
        tenor_months:
          draft.tenor_months === "" ? null : numberValue(draft.tenor_months),
        tenor_days:
          draft.tenor_days === "" ? null : numberValue(draft.tenor_days),
        interest_per_month:
          draft.interest_per_month === ""
            ? null
            : numberValue(draft.interest_per_month),
        processing_fee_percentage:
          draft.processing_fee_percentage === ""
            ? null
            : numberValue(draft.processing_fee_percentage),
        disbursed_date: draft.disbursed_date || null,
      };

      const response = await fetch(
        draft.id
          ? `${API_BASE}/lender/manage-cases/cases/${draft.id}`
          : `${API_BASE}/lender/manage-cases/cases`,
        {
          method: draft.id ? "PUT" : "POST",
          headers: authHeaders,
          body: JSON.stringify(payload),
        },
      );
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json.message || "Unable to save case.");
      }

      const savedCase = normaliseCase(json.case);
      setCases((current) => {
        const exists = current.some((item) => item.id === savedCase.id);
        if (exists) {
          return current.map((item) =>
            item.id === savedCase.id ? savedCase : item,
          );
        }
        return [savedCase, ...current];
      });
      setDraft(savedCase);
      setMode("master");
      message.success(json.message || "Case saved.");
    } catch (error) {
      message.error(error.message || "Unable to save case.");
    } finally {
      setSaving(false);
    }
  };

  const deleteCase = async (caseItem) => {
    Modal.confirm({
      title: "Delete case?",
      content: `${caseItem.borrower_name} will be removed from Manage Cases.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        const response = await fetch(
          `${API_BASE}/lender/manage-cases/cases/${caseItem.id}`,
          {
            method: "DELETE",
            headers: authHeaders,
          },
        );
        if (!response.ok) {
          message.error("Unable to delete case.");
          return;
        }
        setCases((current) =>
          current.filter((item) => item.id !== caseItem.id),
        );
        if (draft.id === caseItem.id) setDraft(emptyDraft);
        message.success("Case deleted.");
      },
    });
  };

  const openFunderModal = (funder = null) => {
    setEditingFunder(funder);
    setFunderForm({
      name: funder?.name || "",
      cost_of_fund_rate: funder?.cost_of_fund_rate ?? "",
    });
    setFunderModalOpen(true);
  };

  const saveFunder = async () => {
    if (!funderForm.name.trim()) {
      message.error("Funder name is required.");
      return;
    }

    try {
      const response = await fetch(
        editingFunder
          ? `${API_BASE}/lender/manage-cases/funders/${editingFunder.id}`
          : `${API_BASE}/lender/manage-cases/funders`,
        {
          method: editingFunder ? "PUT" : "POST",
          headers: authHeaders,
          body: JSON.stringify({
            name: funderForm.name,
            cost_of_fund_rate:
              funderForm.cost_of_fund_rate === ""
                ? null
                : numberValue(funderForm.cost_of_fund_rate),
            is_active: true,
          }),
        },
      );
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json.message || "Unable to save funder.");
      }

      setFunders((current) => {
        const exists = current.some((item) => item.id === json.funder.id);
        if (exists) {
          return current.map((item) =>
            item.id === json.funder.id ? json.funder : item,
          );
        }
        return [...current, json.funder].sort((a, b) =>
          a.name.localeCompare(b.name),
        );
      });
      setFunderModalOpen(false);
      message.success(json.message || "Funder saved.");
    } catch (error) {
      message.error(error.message || "Unable to save funder.");
    }
  };

  const approvedAmount = numberValue(draft.approved_amount);
  const exposureTotal = draft.funder_exposures.reduce(
    (total, exposure) => total + numberValue(exposure.exposure_amount),
    0,
  );

  if (loading) {
    return (
      <div className="manage-cases-page">
        <Spin /> Loading Manage Cases...
      </div>
    );
  }

  return (
    <div className="manage-cases-page">
      <div className="manage-cases-header">
        <div>
          <h1>Manage Cases</h1>
          <p>
            Create funders, key in SmartLend-managed cases, generate repayment
            schedules, and keep the master list editable for bookkeeping
            adjustments.
          </p>
        </div>
        <div className="manage-cases-actions">
          <Button icon={<SyncOutlined />} onClick={loadData}>
            Refresh
          </Button>
          <Button icon={<PlusOutlined />} onClick={startNewCase} type="primary">
            New Case
          </Button>
        </div>
      </div>

      <div className="manage-cases-mobile-note">
        This table is wide. Scroll sideways to edit all calculated columns.
      </div>

      <div className="manage-cases-grid">
        <aside className="manage-cases-panel">
          <div className="manage-cases-panel-header">
            <div>
              <h2>Funders</h2>
              <span>{funders.length} profiles</span>
            </div>
            <Button
              icon={<PlusOutlined />}
              size="small"
              onClick={() => openFunderModal()}
            >
              Add
            </Button>
          </div>
          <div className="manage-cases-panel-body">
            {funders.length === 0 ? (
              <div className="manage-cases-empty">No funders created yet.</div>
            ) : (
              <div className="manage-cases-funder-list">
                {funders.map((funder) => (
                  <div className="manage-cases-funder-item" key={funder.id}>
                    <div>
                      <strong>{funder.name}</strong>
                      <span>
                        Cost of fund: {funder.cost_of_fund_rate || 0}%
                      </span>
                      <span className="manage-cases-funder-unrepaid">
                        Unrepaid funds:{" "}
                        {money(
                          funderUnrepaidTotals.get(String(funder.id)) || 0,
                        )}
                      </span>
                    </div>
                    <Button
                      icon={<EditOutlined />}
                      size="small"
                      onClick={() => openFunderModal(funder)}
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="manage-cases-divider" />

            <div
              className="manage-cases-panel-header"
              style={{ padding: 0, border: 0 }}
            >
              <div>
                <h2>Cases</h2>
                <span>{cases.length} saved</span>
              </div>
              <Button size="small" onClick={() => setMode("master")}>
                Master List
              </Button>
            </div>
            <div className="manage-cases-funder-list">
              {cases.length === 0 ? (
                <div className="manage-cases-empty">No cases saved yet.</div>
              ) : (
                cases.map((caseItem) => (
                  <button
                    type="button"
                    className={`manage-cases-case-button ${
                      draft.id === caseItem.id ? "is-active" : ""
                    }`}
                    key={caseItem.id}
                    onClick={() => editCase(caseItem)}
                  >
                    <strong>{caseItem.borrower_name}</strong>
                    <span>
                      {caseItem.loan_ref || "No loan ref"} -{" "}
                      {money(caseItem.approved_amount)}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        </aside>

        <section className="manage-cases-panel">
          {mode === "master" ? (
            <>
              <div className="manage-cases-panel-header">
                <div>
                  <h2>Master List</h2>
                  <span>{flatMasterRows.length} installment rows</span>
                </div>
              </div>
              <div className="manage-cases-panel-body">
                {flatMasterRows.length === 0 ? (
                  <div className="manage-cases-empty">
                    Confirm and save a case to populate the master list.
                  </div>
                ) : (
                  <MasterListTable
                    rows={flatMasterRows}
                    cases={cases}
                    onEditCase={(caseId) => {
                      const caseItem = cases.find((item) => item.id === caseId);
                      if (caseItem) editCase(caseItem);
                    }}
                    onDeleteCase={deleteCase}
                  />
                )}
              </div>
            </>
          ) : (
            <>
              <div className="manage-cases-panel-header">
                <div>
                  <h2>{draft.id ? "Edit Case" : "New Case"}</h2>
                  <span>Borrower name is the only required field.</span>
                </div>
                <Space>
                  {draft.id ? (
                    <Button danger onClick={() => deleteCase(draft)}>
                      Delete
                    </Button>
                  ) : null}
                  <Button onClick={() => setMode("master")}>Cancel</Button>
                  <Button
                    icon={<SaveOutlined style={{ color: "white" }} />}
                    // style={{color: "white"}}
                    loading={saving}
                    onClick={saveCase}
                    type="primary"
                  >
                    Save Case
                  </Button>
                </Space>
              </div>
              <div className="manage-cases-panel-body">
                <CaseForm
                  draft={draft}
                  updateDraft={updateDraft}
                  borrowerSuggestions={borrowerSuggestions}
                  loanRefSuggestions={loanRefSuggestions}
                  funders={funders}
                  selectedFunderIds={selectedFunderIds}
                  selectedFunders={selectedFunders}
                  setSelectedFunders={setSelectedFunders}
                  updateExposure={updateExposure}
                  openCreditModal={openCreditModal}
                />

                <div className="manage-cases-summary">
                  <SummaryCard
                    label="Approved amount"
                    value={money(approvedAmount)}
                  />
                  <SummaryCard
                    label="Funder exposure"
                    value={money(exposureTotal)}
                  />
                  <SummaryCard
                    label="Exposure difference"
                    value={money(approvedAmount - exposureTotal)}
                  />
                  <SummaryCard
                    label="Loan end date"
                    value={formatDate(
                      draft.master_rows?.[draft.master_rows.length - 1]
                        ?.loan_end_date,
                    )}
                  />
                </div>

                <Button icon={<SyncOutlined />} onClick={recalculateSchedules}>
                  Confirm Loan Details and Generate Installments
                </Button>

                {draft.installments.length > 0 ? (
                  <>
                    <div className="manage-cases-divider" />
                    <EditableMasterRowsTable
                      rows={draft.master_rows}
                      updateMasterRow={updateMasterRow}
                      updateFunderObligation={updateFunderObligation}
                    />
                  </>
                ) : null}
              </div>
            </>
          )}
        </section>
      </div>

      <Modal
        className="manage-cases-modal"
        open={funderModalOpen}
        title={editingFunder ? "Edit Funder" : "Add Funder"}
        onCancel={() => setFunderModalOpen(false)}
        onOk={saveFunder}
        okText="Save"
      >
        <div className="manage-cases-field">
          <label>Funder name</label>
          <Input
            value={funderForm.name}
            onChange={(event) =>
              setFunderForm((current) => ({
                ...current,
                name: event.target.value,
              }))
            }
          />
        </div>
        <div className="manage-cases-field" style={{ marginTop: 12 }}>
          <label>Cost of fund per month (%)</label>
          <InputNumber
            min={0}
            max={100}
            style={{ width: "100%" }}
            value={funderForm.cost_of_fund_rate}
            onChange={(value) =>
              setFunderForm((current) => ({
                ...current,
                cost_of_fund_rate: value ?? "",
              }))
            }
          />
        </div>
      </Modal>

      <CreditAssessmentModal
        open={creditModalOpen}
        inputs={creditInputs}
        result={creditResult}
        onCancel={() => setCreditModalOpen(false)}
        onApply={applyCreditAssessment}
        onChange={(field, value) =>
          setCreditInputs((current) => ({ ...current, [field]: value }))
        }
      />
    </div>
  );
};

const rentlyToday = () => new Date().toISOString().slice(0, 10);

const rentlyId = (prefix) =>
  `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const rentlyBlankProfile = () => ({
  id: rentlyId("profile"),
  name: "",
  companyName: "",
  contact: "",
  email: "",
  uen: "",
  address: "",
  notes: "",
  documents: {
    bankStatements: [],
    cbsReport: [],
    mlcbReport: [],
    sccbReports: [],
  },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

const rentlyBlankCase = (profiles = []) => ({
  id: rentlyId("case"),
  clientProfileId: profiles[0]?.id || "",
  loanAmount: "",
  inceptionDate: rentlyToday(),
  status: "Pending",
  interestPercentage: "",
  interestBasis: "p2p",
  processingFeeEnabled: false,
  processingFeeMode: "percentage",
  processingFeeValue: "",
  referralProfitEnabled: false,
  referralProfitMode: "percentage",
  referralProfitValue: "",
  bankerName: "",
  bankerContact: "",
  repayments: [],
  remarks: [],
  trail: [
    {
      id: rentlyId("trail"),
      at: new Date().toISOString(),
      action: "Case draft created",
    },
  ],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

const rentlyAddDays = (dateValue, days) => {
  const date = new Date(dateValue || rentlyToday());
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
};

const rentlyAddMonths = (dateValue, months) => {
  const date = new Date(dateValue || rentlyToday());
  date.setMonth(date.getMonth() + months);
  return date.toISOString().slice(0, 10);
};

const rentlyRepaymentDueDate = (startDate, frequency, period) => {
  if (frequency === "weekly") return rentlyAddDays(startDate, period * 7);
  if (frequency === "bi_weekly") return rentlyAddDays(startDate, period * 14);
  return rentlyAddMonths(startDate, period);
};

const rentlyDaysBetween = (start, end) => {
  if (!start || !end) return 0;
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return 0;
  }
  return Math.max(
    0,
    Math.ceil(
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
    ),
  );
};

const rentlyInterestForRepayment = ({ basePrincipal, interestPercentage }) => {
  const rate = numberValue(interestPercentage) / 100;
  if (!rate) return 0;
  return roundMoney(numberValue(basePrincipal) * rate);
};

const rentlyTotalRepayment = (repayment) =>
  roundMoney(
    numberValue(repayment?.amount) + numberValue(repayment?.interestEarned),
  );

const rentlyNormalizeInterestBasis = (value) => {
  if (value === "rolling") return "rolling";
  return "p2p";
};

const rentlyFeeAmount = (enabled, mode, value, loanAmount) => {
  if (!enabled) return 0;
  return mode === "percentage"
    ? roundMoney(loanAmount * (numberValue(value) / 100))
    : roundMoney(numberValue(value));
};

const rentlyDisbursementAmount = (caseItem) => {
  const loanAmount = numberValue(caseItem?.loanAmount);
  const processingFee = rentlyFeeAmount(
    Boolean(caseItem?.processingFeeEnabled),
    caseItem?.processingFeeMode,
    caseItem?.processingFeeValue,
    loanAmount,
  );
  return roundMoney(Math.max(loanAmount - processingFee, 0));
};

const rentlyCaseWithTrail = (caseItem, action) => ({
  ...caseItem,
  updatedAt: new Date().toISOString(),
  trail: [
    ...(Array.isArray(caseItem.trail) ? caseItem.trail : []),
    {
      id: rentlyId("trail"),
      at: new Date().toISOString(),
      action,
    },
  ],
});

const rentlyRepaymentStatus = (repayment) => {
  if (repayment.paid) {
    return numberValue(repayment.lateDays) > 0
      ? `Paid late by ${repayment.lateDays} day${repayment.lateDays === 1 ? "" : "s"}`
      : "Paid";
  }
  const overdueDays = rentlyDaysBetween(repayment.dueDate, rentlyToday());
  return overdueDays > 0
    ? `Overdue by ${overdueDays} day${overdueDays === 1 ? "" : "s"}`
    : "Not paid";
};

const rentlyTenorMonths = (caseItem) => {
  const repayments = Array.isArray(caseItem.repayments)
    ? caseItem.repayments
    : [];
  if (!caseItem.inceptionDate || repayments.length === 0) return "-";
  const dueDates = repayments
    .map((item) => item.dueDate)
    .filter(Boolean)
    .sort();
  const lastDueDate = dueDates[dueDates.length - 1];
  if (!lastDueDate) return "-";
  const days = rentlyDaysBetween(caseItem.inceptionDate, lastDueDate);
  const months = Math.max(0, roundMoney(days / 30.44));
  return `${months} month${months === 1 ? "" : "s"}`;
};

const RentlyManageCases = () => {
  const userEmail = Cookies.get("email") || "rently-user";
  const authHeaders = useMemo(
    () => ({
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${Cookies.get("token")}`,
    }),
    [],
  );
  const [loading, setLoading] = useState(true);
  const [portalAccount, setPortalAccount] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [cases, setCases] = useState([]);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [caseModalOpen, setCaseModalOpen] = useState(false);
  const [profileDraft, setProfileDraft] = useState(rentlyBlankProfile);
  const [profileFilesDraft, setProfileFilesDraft] = useState({
    bankStatements: [],
    cbsReport: [],
    mlcbReport: [],
  });
  const [caseDraft, setCaseDraft] = useState(() => rentlyBlankCase([]));
  const [caseRemarkDraft, setCaseRemarkDraft] = useState("");
  const [repaymentCount, setRepaymentCount] = useState(6);
  const [repaymentFrequency, setRepaymentFrequency] = useState("monthly");
  const [selectedCaseId, setSelectedCaseId] = useState("");
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const [repaymentModalCaseId, setRepaymentModalCaseId] = useState("");
  const [caseTableView, setCaseTableView] = useState("cases");
  const [statusFilter, setStatusFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [profileSearch, setProfileSearch] = useState("");
  const [remarkText, setRemarkText] = useState("");
  const [profileReport, setProfileReport] = useState(null);
  const [profileReportLoading, setProfileReportLoading] = useState(false);
  const [savingProfileReportScenario, setSavingProfileReportScenario] =
    useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingCase, setSavingCase] = useState(false);
  const [retrievingSccb, setRetrievingSccb] = useState(false);
  const [sccbModalReport, setSccbModalReport] = useState(null);

  const loadRentlyData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/lender/rently/manage-cases`, {
        method: "GET",
        headers: authHeaders,
      });
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || "Unable to load Rently manage cases.");
      }

      setPortalAccount(json.portal_account || null);
      setProfiles(json.profiles || []);
      setCases(json.cases || []);
      setSelectedCaseId((current) =>
        current &&
        (json.cases || []).some((caseItem) => caseItem.id === current)
          ? current
          : "",
      );
      setSelectedProfileId((current) =>
        current &&
        (json.profiles || []).some((profile) => profile.id === current)
          ? current
          : "",
      );
    } catch (error) {
      message.error(error.message || "Unable to load Rently manage cases.");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    loadRentlyData();
  }, [loadRentlyData]);

  const profileById = useMemo(() => {
    const map = new Map();
    profiles.forEach((profile) => map.set(profile.id, profile));
    return map;
  }, [profiles]);

  const selectedCase = useMemo(
    () => cases.find((caseItem) => caseItem.id === selectedCaseId) || null,
    [cases, selectedCaseId],
  );

  const repaymentModalCase = useMemo(
    () =>
      cases.find((caseItem) => caseItem.id === repaymentModalCaseId) || null,
    [cases, repaymentModalCaseId],
  );

  const selectedProfile = useMemo(() => {
    if (selectedCase) {
      return profileById.get(selectedCase.clientProfileId) || null;
    }
    if (selectedProfileId) {
      return profileById.get(selectedProfileId) || null;
    }
    return null;
  }, [profileById, selectedCase, selectedProfileId]);

  const loadProfileReport = useCallback(
    async (profileId, options = {}) => {
      if (!profileId) {
        setProfileReport(null);
        return;
      }

      if (!options.background) {
        setProfileReportLoading(true);
      }
      try {
        const response = await fetch(
          `${API_BASE}/lender/rently/client-profiles/${profileId}/report`,
          {
            method: "GET",
            headers: authHeaders,
          },
        );
        const json = await response.json();
        if (!response.ok) {
          throw new Error(
            json?.message || "Unable to load client lending report.",
          );
        }
        setProfileReport(json);
      } catch (error) {
        if (!options.background) {
          setProfileReport(null);
        }
        if (!options.silent) {
          message.error(
            error.message || "Unable to load client lending report.",
          );
        }
      } finally {
        if (!options.background) {
          setProfileReportLoading(false);
        }
      }
    },
    [authHeaders],
  );

  const saveProfileReportScenario = useCallback(
    async (scenario) => {
      if (!selectedProfile?.id) {
        return;
      }

      setSavingProfileReportScenario(true);
      try {
        const response = await fetch(
          `${API_BASE}/lender/rently/client-profiles/${selectedProfile.id}/report-scenario`,
          {
            method: "PUT",
            headers: authHeaders,
            body: JSON.stringify({ scenario }),
          },
        );
        const json = await response.json();
        if (!response.ok) {
          throw new Error(json?.message || "Unable to save report scenario.");
        }
      } catch (error) {
        message.error(error.message || "Unable to save report scenario.");
      } finally {
        setSavingProfileReportScenario(false);
      }
    },
    [authHeaders, selectedProfile?.id],
  );

  useEffect(() => {
    if (!selectedProfile?.id) {
      setProfileReport(null);
      return;
    }

    loadProfileReport(selectedProfile.id);
  }, [loadProfileReport, selectedProfile?.id]);

  const hasParsingProfileDocuments = useMemo(
    () => rentlyHasParsingDocuments(selectedProfile, profileReport),
    [profileReport, selectedProfile],
  );

  useEffect(() => {
    if (!selectedProfile?.id || !hasParsingProfileDocuments) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      loadProfileReport(selectedProfile.id, {
        silent: true,
        background: true,
      });
    }, 10000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [hasParsingProfileDocuments, loadProfileReport, selectedProfile?.id]);

  const analytics = useMemo(() => {
    const activeCases = cases.filter((item) => item.status === "Active");
    const totalDisbursed = cases
      .filter((item) =>
        ["Active", "Completed", "Accepted"].includes(item.status),
      )
      .reduce((total, item) => total + numberValue(item.loanAmount), 0);
    const allRepayments = cases.flatMap((caseItem) =>
      (caseItem.repayments || []).map((repayment) => ({
        ...repayment,
        caseItem,
      })),
    );
    const overdueRepayments = allRepayments.filter(
      (repayment) =>
        !repayment.paid &&
        rentlyDaysBetween(repayment.dueDate, rentlyToday()) > 0,
    );
    const currentMonth = rentlyToday().slice(0, 7);
    const monthlyRepayments = allRepayments
      .filter((repayment) =>
        String(repayment.dueDate || "").startsWith(currentMonth),
      )
      .reduce((total, repayment) => total + numberValue(repayment.amount), 0);
    const interestYtd = allRepayments.reduce(
      (total, repayment) => total + numberValue(repayment.interestEarned),
      0,
    );

    return {
      activeCases: activeCases.length,
      totalDisbursed,
      interestYtd,
      monthlyRepayments,
      overdueAmount: overdueRepayments.reduce(
        (total, repayment) => total + numberValue(repayment.amount),
        0,
      ),
      overdueCount: overdueRepayments.length,
    };
  }, [cases]);

  const statusCounts = useMemo(() => {
    const counts = { All: cases.length };
    RENTLY_CASE_STATUSES.forEach((status) => {
      counts[status] = cases.filter(
        (caseItem) => caseItem.status === status,
      ).length;
    });
    return counts;
  }, [cases]);

  const filteredCases = useMemo(() => {
    const query = search.trim().toLowerCase();
    return cases.filter((caseItem) => {
      const profile = profileById.get(caseItem.clientProfileId);
      const matchesStatus =
        statusFilter === "All" || caseItem.status === statusFilter;
      const matchesSearch =
        !query ||
        [profile?.name, profile?.companyName, profile?.email, caseItem.status]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));
      return matchesStatus && matchesSearch;
    });
  }, [cases, profileById, search, statusFilter]);

  const filteredProfiles = useMemo(() => {
    const query = profileSearch.trim().toLowerCase();
    if (!query) return profiles;

    return profiles.filter((profile) =>
      [
        profile.name,
        profile.companyName,
        profile.company_name,
        profile.uen,
        profile.email,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  }, [profileSearch, profiles]);

  const monthBars = useMemo(() => {
    const labels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];
    const values = labels.map((label, index) => {
      const month = String(index + 1).padStart(2, "0");
      return cases
        .filter(
          (caseItem) =>
            String(caseItem.inceptionDate || "").slice(5, 7) === month,
        )
        .reduce(
          (total, caseItem) => total + numberValue(caseItem.loanAmount),
          0,
        );
    });
    const max = Math.max(...values, 1);
    return labels.map((label, index) => ({
      label,
      value: values[index],
      height: Math.max(18, Math.round((values[index] / max) * 150)),
    }));
  }, [cases]);

  const openProfileModal = (profile = null) => {
    setProfileDraft(profile ? { ...profile } : rentlyBlankProfile());
    setProfileFilesDraft({
      bankStatements: [],
      cbsReport: [],
      mlcbReport: [],
    });
    setProfileModalOpen(true);
  };

  const openProfileDrawer = (profile, caseId = "") => {
    if (!profile?.id) return;
    setSelectedProfileId(profile.id);
    setSelectedCaseId(caseId);
    setProfileDrawerOpen(true);
  };

  const openCaseModal = (caseItem = null, profileOverride = null) => {
    const defaultProfile = profileOverride || selectedProfile || profiles[0];
    setCaseDraft(
      caseItem
        ? { ...caseItem, repayments: [...(caseItem.repayments || [])] }
        : rentlyBlankCase(defaultProfile ? [defaultProfile] : profiles),
    );
    setCaseRemarkDraft("");
    setRepaymentCount(caseItem?.repayments?.length || 6);
    setCaseModalOpen(true);
  };

  const profilePayloadForApi = (profile) => ({
    name: profile.name?.trim() || "",
    company_name:
      profile.companyName?.trim() || profile.company_name?.trim() || "",
    contact: profile.contact?.trim() || "",
    email: profile.email?.trim() || "",
    uen: profile.uen?.trim() || "",
    address: profile.address?.trim() || "",
    notes: profile.notes?.trim() || "",
  });

  const casePayloadForApi = (caseItem) => ({
    client_profile_id: caseItem.clientProfileId,
    status: caseItem.status,
    loan_amount: caseItem.loanAmount === "" ? null : caseItem.loanAmount,
    inception_date: caseItem.inceptionDate || null,
    interest_percentage:
      caseItem.interestPercentage === "" ? null : caseItem.interestPercentage,
    interest_basis: rentlyNormalizeInterestBasis(caseItem.interestBasis),
    processing_fee_enabled: Boolean(caseItem.processingFeeEnabled),
    processing_fee_mode: caseItem.processingFeeMode || "percentage",
    processing_fee_value:
      caseItem.processingFeeValue === "" ? null : caseItem.processingFeeValue,
    referral_profit_enabled: Boolean(caseItem.referralProfitEnabled),
    referral_profit_mode: caseItem.referralProfitMode || "percentage",
    referral_profit_value:
      caseItem.referralProfitValue === "" ? null : caseItem.referralProfitValue,
    banker_name: caseItem.bankerName?.trim() || "",
    banker_contact: caseItem.bankerContact?.trim() || "",
    repayments: caseItem.repayments || [],
    remarks: caseItem.remarks || [],
    trail: caseItem.trail || [],
  });

  const uploadProfileDocuments = async (profileId) => {
    const uploads = [
      {
        documentType: "bank_statement",
        files: profileFilesDraft.bankStatements,
      },
      {
        documentType: "cbs",
        files: profileFilesDraft.cbsReport,
      },
      {
        documentType: "mlcb",
        files: profileFilesDraft.mlcbReport,
      },
    ].filter((upload) => upload.files.length > 0);

    for (const upload of uploads) {
      const formData = new FormData();
      formData.append("document_type", upload.documentType);
      upload.files.forEach((file) => formData.append("files[]", file));

      if (upload.documentType === "cbs" && upload.files[0]) {
        const text = await pdfToText(upload.files[0]);
        const parsedPayload = parseRentlyCbsData(text);
        if (!parsedPayload) {
          throw new Error(
            "CBS report not detected. Please upload the original CBS PDF.",
          );
        }
        formData.append("parsed_payload", JSON.stringify(parsedPayload));
      }

      const response = await fetch(
        `${API_BASE}/lender/rently/client-profiles/${profileId}/documents`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: formData,
        },
      );
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || "Unable to upload profile documents.");
      }
    }
  };

  const retrieveSccbBizCheckPlus = (profile) => {
    if (!profile?.id) return;
    if (
      !textValue(profile.companyName || profile.company_name).trim() ||
      !textValue(profile.uen).trim()
    ) {
      message.error(
        "Company name and UEN are required before retrieving SCCB.",
      );
      return;
    }

    Modal.confirm({
      title: "Retrieve SCCB Biz Check Plus",
      content: "This retrieval will use a total of 20 tokens.",
      okText: "Confirm",
      cancelText: "Cancel",
      onOk: async () => {
        setRetrievingSccb(true);
        try {
          const response = await fetch(
            `${API_BASE}/lender/rently/client-profiles/${profile.id}/sccb/biz-check-plus`,
            {
              method: "POST",
              headers: authHeaders,
              body: JSON.stringify({}),
            },
          );
          const json = await response.json();
          if (!response.ok) {
            throw new Error(
              json?.message || "Unable to retrieve SCCB Biz Check Plus.",
            );
          }

          if (json.portal_account) {
            setPortalAccount(json.portal_account);
          }
          if (json.profile) {
            setProfiles((current) =>
              current.map((item) =>
                item.id === json.profile.id ? json.profile : item,
              ),
            );
          }
          message.success("SCCB Biz Check Plus retrieved.");
          await loadProfileReport(profile.id, { silent: true });
        } catch (error) {
          message.error(
            error.message || "Unable to retrieve SCCB Biz Check Plus.",
          );
        } finally {
          setRetrievingSccb(false);
        }
      },
    });
  };

  const saveProfile = async () => {
    if (!profileDraft.name.trim()) {
      message.error("Client name is required.");
      return;
    }

    setSavingProfile(true);
    try {
      const exists = profiles.some((profile) => profile.id === profileDraft.id);
      const response = await fetch(
        exists
          ? `${API_BASE}/lender/rently/client-profiles/${profileDraft.id}`
          : `${API_BASE}/lender/rently/client-profiles`,
        {
          method: exists ? "PUT" : "POST",
          headers: authHeaders,
          body: JSON.stringify(profilePayloadForApi(profileDraft)),
        },
      );
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || "Unable to save client profile.");
      }

      const savedProfile = json.profile;
      await uploadProfileDocuments(savedProfile.id);
      setSelectedProfileId(savedProfile.id);
      setSelectedCaseId("");
      setProfileModalOpen(false);
      message.success("Client profile saved.");
      await loadRentlyData();
      await loadProfileReport(savedProfile.id, { silent: true });
    } catch (error) {
      message.error(error.message || "Unable to save client profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  const generateRepayments = () => {
    const count = Math.max(1, Number(repaymentCount) || 1);
    const loanAmount = numberValue(caseDraft.loanAmount);
    const perRepayment = roundMoney(loanAmount / count);
    const interestBasis = rentlyNormalizeInterestBasis(caseDraft.interestBasis);
    const repayments = Array.from({ length: count }, (_, index) => {
      const period = index + 1;
      const dueDate = rentlyRepaymentDueDate(
        caseDraft.inceptionDate,
        repaymentFrequency,
        period,
      );
      const remainingPrincipal = Math.max(loanAmount - perRepayment * index, 0);
      const interestEarned = rentlyInterestForRepayment({
        basePrincipal:
          interestBasis === "rolling" ? remainingPrincipal : loanAmount,
        interestPercentage: caseDraft.interestPercentage,
      });

      return {
        id: rentlyId("repayment"),
        period,
        dueDate,
        amount: perRepayment,
        interestEarned,
        totalRepayment: roundMoney(perRepayment + interestEarned),
        paid: false,
        paidDate: "",
        receivedDate: "",
        lateDays: 0,
      };
    });

    setCaseDraft((current) =>
      rentlyCaseWithTrail(
        {
          ...current,
          repayments,
        },
        `Generated ${count} ${repaymentFrequency} repayment${
          count === 1 ? "" : "s"
        }`,
      ),
    );
  };

  const updateCaseDraftRepayment = (repaymentId, patch) => {
    setCaseDraft((current) => ({
      ...current,
      repayments: (current.repayments || []).map((repayment) =>
        repayment.id === repaymentId
          ? {
              ...repayment,
              ...patch,
              totalRepayment: rentlyTotalRepayment({ ...repayment, ...patch }),
            }
          : repayment,
      ),
    }));
  };

  const saveCase = async () => {
    if (!caseDraft.clientProfileId) {
      message.error("Link the case to a client profile.");
      return;
    }

    const exists = cases.some((caseItem) => caseItem.id === caseDraft.id);
    let caseToSave = rentlyCaseWithTrail(
      caseDraft,
      exists ? "Case details updated" : "Case created",
    );
    if (caseRemarkDraft.trim()) {
      caseToSave = rentlyCaseWithTrail(
        {
          ...caseToSave,
          remarks: [
            ...(Array.isArray(caseToSave.remarks) ? caseToSave.remarks : []),
            {
              id: rentlyId("remark"),
              at: new Date().toISOString(),
              text: caseRemarkDraft.trim(),
              author: userEmail,
            },
          ],
        },
        "Remark added",
      );
    }

    setSavingCase(true);
    try {
      const response = await fetch(
        exists
          ? `${API_BASE}/lender/rently/cases/${caseToSave.id}`
          : `${API_BASE}/lender/rently/cases`,
        {
          method: exists ? "PUT" : "POST",
          headers: authHeaders,
          body: JSON.stringify(casePayloadForApi(caseToSave)),
        },
      );
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json?.message || "Unable to save case.");
      }
      setSelectedCaseId(json.case.id);
      setSelectedProfileId(json.case.clientProfileId);
      setCaseModalOpen(false);
      setCaseRemarkDraft("");
      message.success("Case saved.");
      await loadRentlyData();
    } catch (error) {
      message.error(error.message || "Unable to save case.");
    } finally {
      setSavingCase(false);
    }
  };

  const persistUpdatedCase = async (nextCase) => {
    const response = await fetch(
      `${API_BASE}/lender/rently/cases/${nextCase.id}`,
      {
        method: "PUT",
        headers: authHeaders,
        body: JSON.stringify(casePayloadForApi(nextCase)),
      },
    );
    const json = await response.json();
    if (!response.ok) {
      throw new Error(json?.message || "Unable to update case.");
    }
    setCases((current) =>
      current.map((caseItem) =>
        caseItem.id === json.case.id ? json.case : caseItem,
      ),
    );
    return json.case;
  };

  const updateSavedCase = async (caseId, updater) => {
    const currentCase = cases.find((caseItem) => caseItem.id === caseId);
    if (!currentCase) return;
    const nextCase = updater(currentCase);
    setCases((current) =>
      current.map((caseItem) => (caseItem.id === caseId ? nextCase : caseItem)),
    );
    try {
      await persistUpdatedCase(nextCase);
    } catch (error) {
      message.error(error.message || "Unable to update case.");
      await loadRentlyData();
    }
  };

  const changeStatus = (caseId, status) => {
    updateSavedCase(caseId, (caseItem) =>
      rentlyCaseWithTrail(
        { ...caseItem, status },
        `Status changed to ${status}`,
      ),
    );
  };

  const markRepaymentPaid = (caseId, repaymentId, paid) => {
    updateSavedCase(caseId, (caseItem) =>
      rentlyCaseWithTrail(
        {
          ...caseItem,
          repayments: (caseItem.repayments || []).map((repayment) => {
            if (repayment.id !== repaymentId) return repayment;
            const receivedDate = paid
              ? repayment.receivedDate || rentlyToday()
              : "";
            return {
              ...repayment,
              paid,
              paidDate: paid ? rentlyToday() : "",
              receivedDate,
              lateDays: paid
                ? rentlyDaysBetween(repayment.dueDate, receivedDate)
                : 0,
            };
          }),
        },
        paid ? "Repayment marked as paid" : "Repayment marked as unpaid",
      ),
    );
  };

  const adjustReceivedDate = (caseId, repaymentId, receivedDate) => {
    updateSavedCase(caseId, (caseItem) =>
      rentlyCaseWithTrail(
        {
          ...caseItem,
          repayments: (caseItem.repayments || []).map((repayment) =>
            repayment.id === repaymentId
              ? {
                  ...repayment,
                  paid: Boolean(receivedDate),
                  paidDate: receivedDate || "",
                  receivedDate,
                  lateDays: receivedDate
                    ? rentlyDaysBetween(repayment.dueDate, receivedDate)
                    : 0,
                }
              : repayment,
          ),
        },
        "Repayment received date adjusted",
      ),
    );
  };

  const addRemark = () => {
    if (!selectedCase || !remarkText.trim()) return;
    updateSavedCase(selectedCase.id, (caseItem) =>
      rentlyCaseWithTrail(
        {
          ...caseItem,
          remarks: [
            ...(Array.isArray(caseItem.remarks) ? caseItem.remarks : []),
            {
              id: rentlyId("remark"),
              at: new Date().toISOString(),
              text: remarkText.trim(),
              author: userEmail,
            },
          ],
        },
        "Remark added",
      ),
    );
    setRemarkText("");
  };

  const deleteCase = (caseId) => {
    Modal.confirm({
      title: "Delete case?",
      content: "This removes the case from the Rently case list.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const response = await fetch(
            `${API_BASE}/lender/rently/cases/${caseId}`,
            {
              method: "DELETE",
              headers: authHeaders,
            },
          );
          const json = await response.json();
          if (!response.ok) {
            throw new Error(json?.message || "Unable to delete case.");
          }
          setCases((current) =>
            current.filter((caseItem) => caseItem.id !== caseId),
          );
          if (selectedCaseId === caseId) setSelectedCaseId("");
          message.success("Case deleted.");
        } catch (error) {
          message.error(error.message || "Unable to delete case.");
        }
      },
    });
  };

  const deleteBankStatement = (submissionId) => {
    if (!selectedProfile?.id) return;

    Modal.confirm({
      title: "Delete bank statement?",
      content:
        "This removes the selected bank statement from this client profile. It will no longer be included in the lending report.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const response = await fetch(
            `${API_BASE}/lender/rently/client-profiles/${selectedProfile.id}/bank-statements/${submissionId}`,
            {
              method: "DELETE",
              headers: authHeaders,
            },
          );
          const json = await response.json();
          if (!response.ok) {
            throw new Error(
              json?.message || "Unable to delete bank statement.",
            );
          }
          message.success("Bank statement deleted.");
          await loadRentlyData();
          await loadProfileReport(selectedProfile.id, { silent: true });
        } catch (error) {
          message.error(error.message || "Unable to delete bank statement.");
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="rently-cases-page">
        <Spin /> Loading Manage Cases...
      </div>
    );
  }

  return (
    <div className="rently-cases-page">
      <div className="rently-cases-header">
        <div>
          <h1>Manage Cases</h1>
          <p>
            Rently client profiles, case stages, repayment tracking, remarks,
            and case trail history.
          </p>
        </div>
        <div className="rently-cases-actions">
          <Button
            onClick={() => openProfileModal()}
            icon={<PlusOutlined />}
            style={{ borderRadius: "200px" }}
          >
            Client Profile
          </Button>
          <Button
            onClick={() =>
              setCaseTableView((current) =>
                current === "cases" ? "clients" : "cases",
              )
            }
          >
            {caseTableView === "cases" ? "View Clients" : "View Cases"}
          </Button>
          <Button
            onClick={() => openCaseModal()}
            icon={<PlusOutlined />}
            type="primary"
          >
            New Case
          </Button>
        </div>
      </div>

      {!portalAccount ? (
        <div className="rently-portal-warning">
          This lender is not linked to a bank statement portal billing account
          yet. Profiles and cases can be saved, but document uploads need an
          admin link in Portal Users first.
        </div>
      ) : (
        <div className="rently-portal-status">
          Portal billing:{" "}
          <strong>{portalAccount.billing_group?.name || "User wallet"}</strong>{" "}
          - {portalAccount.tokens_remaining} tokens available
        </div>
      )}

      <div className="rently-kpi-strip">
        <RentlyKpiCard
          hero
          label="Total Disbursed"
          value={money(analytics.totalDisbursed)}
          delta={`across ${cases.length} case${cases.length === 1 ? "" : "s"}`}
        />
        <RentlyKpiCard
          label="Active Cases"
          value={analytics.activeCases}
          delta={`${statusCounts.Pending || 0} pending`}
        />
        <RentlyKpiCard
          label="Interest YTD"
          value={money(analytics.interestYtd)}
          delta="from generated repayment tables"
        />
        <RentlyKpiCard
          label={`Repayments (${new Date().toLocaleString("en", { month: "short" })})`}
          value={money(analytics.monthlyRepayments)}
          delta="scheduled this month"
        />
        <RentlyKpiCard
          danger
          label="Overdue"
          value={money(analytics.overdueAmount)}
          delta={`${analytics.overdueCount} repayment${
            analytics.overdueCount === 1 ? "" : "s"
          }`}
        />
      </div>

      {caseTableView === "cases" ? (
        <div className="rently-tabs">
          {["All", ...RENTLY_CASE_STATUSES].map((status) => (
            <button
              type="button"
              className={`rently-tab ${statusFilter === status ? "is-active" : ""}`}
              key={status}
              onClick={() => setStatusFilter(status)}
            >
              {status} <span>{statusCounts[status] || 0}</span>
            </button>
          ))}
        </div>
      ) : null}

      <div className="rently-filter-row">
        {caseTableView === "cases" ? (
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search clients, companies, emails, statuses..."
          />
        ) : (
          <Input
            value={profileSearch}
            onChange={(event) => setProfileSearch(event.target.value)}
            placeholder="Search client name, company name, or UEN..."
          />
        )}
        <span>
          Showing{" "}
          <strong>
            {caseTableView === "cases"
              ? filteredCases.length
              : filteredProfiles.length}
          </strong>{" "}
          of{" "}
          <strong>
            {caseTableView === "cases" ? cases.length : profiles.length}
          </strong>
        </span>
      </div>

      <div className="rently-table-card">
        {caseTableView === "clients" ? (
          filteredProfiles.length === 0 ? (
            <div className="rently-empty">
              No client profiles match this view.
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Company</th>
                  <th>UEN / ID</th>
                  <th>Contact</th>
                  <th>Email</th>
                  <th>Cases</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredProfiles.map((profile) => {
                  const profileCases = cases.filter(
                    (caseItem) => caseItem.clientProfileId === profile.id,
                  );
                  return (
                    <tr
                      key={profile.id}
                      onClick={() => openProfileDrawer(profile)}
                    >
                      <td>
                        <div className="rently-principal">
                          <span className="rently-avatar">
                            {String(profile.name || "NA")
                              .slice(0, 2)
                              .toUpperCase()}
                          </span>
                          <div>
                            <strong>{profile.name || "-"}</strong>
                            <small>{profile.notes || "Client profile"}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        {profile.companyName || profile.company_name || "-"}
                      </td>
                      <td>{profile.uen || "-"}</td>
                      <td>{profile.contact || "-"}</td>
                      <td>{profile.email || "-"}</td>
                      <td>{profileCases.length}</td>
                      <td onClick={(event) => event.stopPropagation()}>
                        <Button
                          size="small"
                          type="primary"
                          onClick={() => {
                            setSelectedProfileId(profile.id);
                            setSelectedCaseId("");
                            openCaseModal(null, profile);
                          }}
                        >
                          Create Case
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        ) : filteredCases.length === 0 ? (
          <div className="rently-empty">
            No cases yet. Create a client profile, then add the first case.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Principal</th>
                <th>Loan Amount</th>
                <th>Inception</th>
                <th>Tenor</th>
                <th>Interest Earned</th>
                <th>Repayment</th>
                <th>Next Due</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredCases.map((caseItem) => {
                const profile = profileById.get(caseItem.clientProfileId);
                const repayments = Array.isArray(caseItem.repayments)
                  ? caseItem.repayments
                  : [];
                const paidCount = repayments.filter(
                  (repayment) => repayment.paid,
                ).length;
                const nextDue = repayments.find((repayment) => !repayment.paid);
                const progress =
                  repayments.length === 0
                    ? 0
                    : Math.round((paidCount / repayments.length) * 100);
                const interestEarned = repayments.reduce(
                  (total, repayment) =>
                    total + numberValue(repayment.interestEarned),
                  0,
                );

                return (
                  <tr
                    className={
                      selectedCase?.id === caseItem.id ? "is-selected" : ""
                    }
                    key={caseItem.id}
                    onClick={() => {
                      openProfileDrawer(profile, caseItem.id);
                    }}
                  >
                    <td>
                      <div className="rently-principal">
                        <span className="rently-avatar">
                          {String(profile?.name || "NA")
                            .slice(0, 2)
                            .toUpperCase()}
                        </span>
                        <div>
                          <strong>{profile?.name || "No profile"}</strong>
                          <small>
                            {profile?.companyName || profile?.email || "-"}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td>{money(caseItem.loanAmount)}</td>
                    <td>{formatDate(caseItem.inceptionDate)}</td>
                    <td>{rentlyTenorMonths(caseItem)}</td>
                    <td className="rently-money-purple">
                      {money(interestEarned)}
                    </td>
                    <td onClick={(event) => event.stopPropagation()}>
                      <button
                        type="button"
                        className="rently-repayment-trigger"
                        onClick={() => setRepaymentModalCaseId(caseItem.id)}
                      >
                        <span className="rently-progress">
                          <span style={{ width: `${progress}%` }} />
                        </span>
                        <small>
                          {progress}% · {paidCount}/{repayments.length || 0}
                        </small>
                      </button>
                    </td>
                    <td>{nextDue ? formatDate(nextDue.dueDate) : "-"}</td>
                    <td onClick={(event) => event.stopPropagation()}>
                      <Select
                        size="small"
                        value={caseItem.status}
                        onChange={(value) => changeStatus(caseItem.id, value)}
                        options={RENTLY_CASE_STATUSES.map((status) => ({
                          label: status,
                          value: status,
                        }))}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="rently-analytics-row">
        <section className="rently-panel">
          <div className="rently-panel-title">Disbursement Trend</div>
          <div className="rently-panel-sub">Loan amount by inception month</div>
          <div className="rently-bars">
            {monthBars.map((bar) => (
              <div className="rently-bar-wrap" key={bar.label}>
                <div className="rently-bar" style={{ height: bar.height }}>
                  <span>
                    {bar.value ? money(bar.value).replace("SGD ", "") : "-"}
                  </span>
                </div>
                <small>{bar.label}</small>
              </div>
            ))}
          </div>
        </section>
        <section className="rently-panel">
          <div className="rently-panel-title">Application Mix</div>
          <div className="rently-panel-sub">
            Accepted, rejected, and pending cases
          </div>
          <div className="rently-donut">
            <div>
              <strong>{statusCounts.Accepted || 0}</strong>
              <span>Accepted</span>
            </div>
          </div>
          <div className="rently-donut-legend">
            <span>
              <i /> {statusCounts.Accepted || 0} Accepted
            </span>
            <span>
              <i className="is-rejected" /> {statusCounts.Rejected || 0}{" "}
              Rejected
            </span>
            <span>
              <i className="is-pending" /> {statusCounts.Pending || 0} Pending
            </span>
          </div>
        </section>
      </div>

      <Drawer
        className="rently-profile-drawer"
        open={profileDrawerOpen && Boolean(selectedProfile)}
        title="Client Profile"
        width="min(1180px, 94vw)"
        onClose={() => setProfileDrawerOpen(false)}
        extra={
          <Button onClick={() => setProfileDrawerOpen(false)}>Close</Button>
        }
        destroyOnClose={false}
      >
        {selectedProfile ? (
          <RentlyCaseDetail
            caseItem={selectedCase}
            profile={selectedProfile}
            profileReport={profileReport}
            profileReportLoading={profileReportLoading}
            savingProfileReportScenario={savingProfileReportScenario}
            onProfileReportScenarioChange={saveProfileReportScenario}
            onEditProfile={() => openProfileModal(selectedProfile)}
            onUploadMoreStatements={() => openProfileModal(selectedProfile)}
            onRetrieveSccb={() => retrieveSccbBizCheckPlus(selectedProfile)}
            onOpenSccbReport={setSccbModalReport}
            retrievingSccb={retrievingSccb}
            onDeleteBankStatement={deleteBankStatement}
            onCreateCase={() => openCaseModal()}
            onEditCase={() => openCaseModal(selectedCase)}
            onDeleteCase={() => selectedCase && deleteCase(selectedCase.id)}
            onMarkPaid={markRepaymentPaid}
            onAdjustReceivedDate={adjustReceivedDate}
            remarkText={remarkText}
            setRemarkText={setRemarkText}
            onAddRemark={addRemark}
          />
        ) : null}
      </Drawer>

      <Modal
        className="rently-modal rently-repayment-modal"
        open={Boolean(repaymentModalCase)}
        title="Repayment Tracking"
        width={980}
        footer={null}
        onCancel={() => setRepaymentModalCaseId("")}
      >
        {repaymentModalCase ? (
          <>
            <div className="rently-repayment-modal-heading">
              <div>
                <strong>
                  {profileById.get(repaymentModalCase.clientProfileId)?.name ||
                    "Client"}
                </strong>
                <span>{money(repaymentModalCase.loanAmount)} loan amount</span>
              </div>
              <Button
                onClick={() =>
                  openProfileDrawer(
                    profileById.get(repaymentModalCase.clientProfileId),
                    repaymentModalCase.id,
                  )
                }
              >
                Open Case
              </Button>
            </div>
            <div className="rently-mini-table">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Due</th>
                    <th>Principal</th>
                    <th>Interest</th>
                    <th>Total</th>
                    <th>Received</th>
                    <th>Information</th>
                    <th>Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {(repaymentModalCase.repayments || []).map((repayment) => (
                    <tr
                      className={
                        !repayment.paid &&
                        rentlyDaysBetween(repayment.dueDate, rentlyToday()) > 0
                          ? "is-overdue"
                          : ""
                      }
                      key={repayment.id}
                    >
                      <td>{repayment.period}</td>
                      <td>{formatDate(repayment.dueDate)}</td>
                      <td>{money(repayment.amount)}</td>
                      <td>{money(repayment.interestEarned)}</td>
                      <td>{money(rentlyTotalRepayment(repayment))}</td>
                      <td>
                        <input
                          type="date"
                          value={repayment.receivedDate || ""}
                          onChange={(event) =>
                            adjustReceivedDate(
                              repaymentModalCase.id,
                              repayment.id,
                              event.target.value,
                            )
                          }
                        />
                      </td>
                      <td>{rentlyRepaymentStatus(repayment)}</td>
                      <td>
                        <input
                          type="checkbox"
                          checked={Boolean(repayment.paid)}
                          onChange={(event) =>
                            markRepaymentPaid(
                              repaymentModalCase.id,
                              repayment.id,
                              event.target.checked,
                            )
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </Modal>

      <Modal
        className="rently-modal"
        open={Boolean(sccbModalReport)}
        title="SCCB Biz Check Plus Report"
        width="92vw"
        footer={null}
        onCancel={() => setSccbModalReport(null)}
      >
        <SccbPdfViewer
          data={
            sccbModalReport?.raw_report_xml || sccbModalReport?.parsed_report
          }
          reportType="company"
          filename="SmartLend_SCCB_Biz_Check_Plus.pdf"
          brand={{
            titleLeft: "SCCB Biz Check Plus",
            titleRight: "In collaboration with SCCB",
          }}
        />
      </Modal>

      <Modal
        className="rently-modal"
        open={profileModalOpen}
        title="Client Profile"
        width={760}
        onCancel={() => setProfileModalOpen(false)}
        onOk={saveProfile}
        okText="Save Profile"
        confirmLoading={savingProfile}
        style={{ zIndex: 1001 }}
      >
        <div className="rently-form-grid">
          <RentlyTextField
            label="Client name"
            value={profileDraft.name}
            onChange={(value) =>
              setProfileDraft((current) => ({ ...current, name: value }))
            }
          />
          <RentlyTextField
            label="Company name"
            value={profileDraft.companyName}
            onChange={(value) =>
              setProfileDraft((current) => ({ ...current, companyName: value }))
            }
          />
          <RentlyTextField
            label="Contact"
            value={profileDraft.contact}
            onChange={(value) =>
              setProfileDraft((current) => ({ ...current, contact: value }))
            }
          />
          <RentlyTextField
            label="Email"
            type="email"
            value={profileDraft.email}
            onChange={(value) =>
              setProfileDraft((current) => ({ ...current, email: value }))
            }
          />
          <RentlyTextField
            label="UEN / ID"
            value={profileDraft.uen}
            onChange={(value) =>
              setProfileDraft((current) => ({ ...current, uen: value }))
            }
          />
          <RentlyTextField
            label="Address"
            value={profileDraft.address}
            onChange={(value) =>
              setProfileDraft((current) => ({ ...current, address: value }))
            }
          />
        </div>
        <div className="rently-form-grid is-files">
          <RentlyFileField
            label="Bank statements"
            multiple
            files={[
              ...(profileDraft.documents?.bankStatements || []),
              ...profileFilesDraft.bankStatements.map((file) => ({
                id: `pending-${file.name}`,
                name: file.name,
              })),
            ]}
            onChange={(files) =>
              setProfileFilesDraft((current) => ({
                ...current,
                bankStatements: Array.from(files || []),
              }))
            }
          />
          <RentlyFileField
            label="CBS report"
            helper="Selecting a new CBS PDF replaces the current CBS report and reparses it."
            files={[
              ...(profileDraft.documents?.cbsReport || []),
              ...profileFilesDraft.cbsReport.map((file) => ({
                id: `pending-${file.name}`,
                name: file.name,
              })),
            ]}
            onChange={(files) =>
              setProfileFilesDraft((current) => ({
                ...current,
                cbsReport: Array.from(files || []).slice(0, 1),
              }))
            }
          />
          <RentlyFileField
            label="MLCB report"
            helper="Selecting a new MLCB PDF replaces the current MLCB report and sends it for OCR again."
            files={[
              ...(profileDraft.documents?.mlcbReport || []),
              ...profileFilesDraft.mlcbReport.map((file) => ({
                id: `pending-${file.name}`,
                name: file.name,
              })),
            ]}
            onChange={(files) =>
              setProfileFilesDraft((current) => ({
                ...current,
                mlcbReport: Array.from(files || []).slice(0, 1),
              }))
            }
          />
        </div>
        <div className="rently-field">
          <label>Notes</label>
          <Input.TextArea
            rows={3}
            value={profileDraft.notes}
            onChange={(event) =>
              setProfileDraft((current) => ({
                ...current,
                notes: event.target.value,
              }))
            }
          />
        </div>
      </Modal>

      <Modal
        className="rently-modal"
        open={caseModalOpen}
        title="Manage Case"
        width={920}
        onCancel={() => setCaseModalOpen(false)}
        onOk={saveCase}
        okText="Save Case"
        confirmLoading={savingCase}
      >
        <div className="rently-form-grid">
          <div className="rently-field">
            <label>Client profile</label>
            <Select
              value={caseDraft.clientProfileId}
              onChange={(value) =>
                setCaseDraft((current) => ({
                  ...current,
                  clientProfileId: value,
                }))
              }
              options={profiles.map((profile) => ({
                label: `${profile.name}${profile.companyName ? ` - ${profile.companyName}` : ""}`,
                value: profile.id,
              }))}
              placeholder="Select a client"
            />
          </div>
          <div className="rently-field">
            <label>Status</label>
            <Select
              value={caseDraft.status}
              onChange={(value) =>
                setCaseDraft((current) =>
                  rentlyCaseWithTrail(
                    { ...current, status: value },
                    `Status changed to ${value}`,
                  ),
                )
              }
              options={RENTLY_CASE_STATUSES.map((status) => ({
                label: status,
                value: status,
              }))}
            />
          </div>
          <RentlyTextField
            label="Loan amount"
            type="number"
            value={caseDraft.loanAmount}
            onChange={(value) =>
              setCaseDraft((current) => ({ ...current, loanAmount: value }))
            }
          />
          <RentlyTextField
            label="Inception date"
            type="date"
            value={caseDraft.inceptionDate}
            onChange={(value) =>
              setCaseDraft((current) => ({ ...current, inceptionDate: value }))
            }
          />
          <RentlyTextField
            label="Interest percentage"
            type="number"
            suffix="%"
            value={caseDraft.interestPercentage}
            onChange={(value) =>
              setCaseDraft((current) => ({
                ...current,
                interestPercentage: value,
              }))
            }
          />
          <div className="rently-field">
            <label>Interest basis</label>
            <Select
              value={rentlyNormalizeInterestBasis(caseDraft.interestBasis)}
              onChange={(value) =>
                setCaseDraft((current) => ({
                  ...current,
                  interestBasis: value,
                }))
              }
              options={RENTLY_INTEREST_BASIS_OPTIONS}
            />
          </div>
          <div className="rently-field">
            <label>Processing fee</label>
            <Select
              value={caseDraft.processingFeeEnabled ? "yes" : "no"}
              onChange={(value) =>
                setCaseDraft((current) => ({
                  ...current,
                  processingFeeEnabled: value === "yes",
                }))
              }
              options={[
                { label: "No processing fee", value: "no" },
                { label: "Yes, add processing fee", value: "yes" },
              ]}
            />
          </div>
          {caseDraft.processingFeeEnabled ? (
            <>
              <div className="rently-field">
                <label>Processing fee type</label>
                <Select
                  value={caseDraft.processingFeeMode || "percentage"}
                  onChange={(value) =>
                    setCaseDraft((current) => ({
                      ...current,
                      processingFeeMode: value,
                    }))
                  }
                  options={RENTLY_AMOUNT_MODE_OPTIONS}
                />
              </div>
              <RentlyTextField
                label="Processing fee value"
                type="number"
                prefix={
                  caseDraft.processingFeeMode === "fixed" ? "SGD" : undefined
                }
                suffix={
                  caseDraft.processingFeeMode === "percentage" ? "%" : undefined
                }
                value={caseDraft.processingFeeValue}
                onChange={(value) =>
                  setCaseDraft((current) => ({
                    ...current,
                    processingFeeValue: value,
                  }))
                }
              />
            </>
          ) : null}
          <div className="rently-field">
            <label>Referral profits</label>
            <Select
              value={caseDraft.referralProfitEnabled ? "yes" : "no"}
              onChange={(value) =>
                setCaseDraft((current) => ({
                  ...current,
                  referralProfitEnabled: value === "yes",
                }))
              }
              options={[
                { label: "No referral profits", value: "no" },
                { label: "Yes, add referral profits", value: "yes" },
              ]}
            />
          </div>
          {caseDraft.referralProfitEnabled ? (
            <>
              <div className="rently-field">
                <label>Referral profit type</label>
                <Select
                  value={caseDraft.referralProfitMode || "percentage"}
                  onChange={(value) =>
                    setCaseDraft((current) => ({
                      ...current,
                      referralProfitMode: value,
                    }))
                  }
                  options={RENTLY_AMOUNT_MODE_OPTIONS}
                />
              </div>
              <RentlyTextField
                label="Referral profit value"
                type="number"
                prefix={
                  caseDraft.referralProfitMode === "fixed" ? "SGD" : undefined
                }
                suffix={
                  caseDraft.referralProfitMode === "percentage"
                    ? "%"
                    : undefined
                }
                value={caseDraft.referralProfitValue}
                onChange={(value) =>
                  setCaseDraft((current) => ({
                    ...current,
                    referralProfitValue: value,
                  }))
                }
              />
            </>
          ) : null}
          <RentlyTextField
            label="Banker name"
            value={caseDraft.bankerName}
            onChange={(value) =>
              setCaseDraft((current) => ({ ...current, bankerName: value }))
            }
          />
          <RentlyTextField
            label="Banker contact"
            value={caseDraft.bankerContact}
            onChange={(value) =>
              setCaseDraft((current) => ({ ...current, bankerContact: value }))
            }
          />
        </div>

        <div className="rently-disbursement-summary">
          <div>
            <div>
              <span>Loan Amount</span>
            </div>
            <strong>{money(caseDraft.loanAmount)}</strong>
          </div>
          <div>
            <div>
              <span>Processing Fee</span>
            </div>
            <strong>
              {money(
                rentlyFeeAmount(
                  Boolean(caseDraft.processingFeeEnabled),
                  caseDraft.processingFeeMode,
                  caseDraft.processingFeeValue,
                  numberValue(caseDraft.loanAmount),
                ),
              )}
            </strong>
          </div>
          <div>
            <div>
              <span>Disbursement Amount</span>
            </div>
            <strong className="is-purple">
              {money(rentlyDisbursementAmount(caseDraft))}
            </strong>
          </div>
        </div>

        <div className="rently-repayment-generator">
          <InputNumber
            min={1}
            value={repaymentCount}
            onChange={(value) => setRepaymentCount(value || 1)}
          />
          <Select
            value={repaymentFrequency}
            onChange={setRepaymentFrequency}
            options={RENTLY_REPAYMENT_FREQUENCIES}
          />
          <Button onClick={generateRepayments} icon={<SyncOutlined />}>
            Generate Repayment Table
          </Button>
        </div>

        {caseDraft.repayments.length > 0 ? (
          <div className="rently-mini-table">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Due date</th>
                  <th>Repayment</th>
                  <th>Interest earned</th>
                  <th>Total repayment</th>
                </tr>
              </thead>
              <tbody>
                {caseDraft.repayments.map((repayment) => (
                  <tr key={repayment.id}>
                    <td>{repayment.period}</td>
                    <td>
                      <input
                        type="date"
                        value={repayment.dueDate}
                        onChange={(event) =>
                          updateCaseDraftRepayment(repayment.id, {
                            dueDate: event.target.value,
                          })
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        value={repayment.amount}
                        onChange={(event) =>
                          updateCaseDraftRepayment(repayment.id, {
                            amount: event.target.value,
                          })
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        value={repayment.interestEarned}
                        onChange={(event) =>
                          updateCaseDraftRepayment(repayment.id, {
                            interestEarned: event.target.value,
                          })
                        }
                      />
                    </td>
                    <td>{money(rentlyTotalRepayment(repayment))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        <div className="rently-field rently-case-remark-field">
          <label>Case remark</label>
          <Input.TextArea
            rows={3}
            value={caseRemarkDraft}
            onChange={(event) => setCaseRemarkDraft(event.target.value)}
            placeholder="Add a dated case remark..."
          />
        </div>
      </Modal>
    </div>
  );
};

const RentlyKpiCard = ({
  label,
  value,
  delta,
  hero = false,
  danger = false,
}) => (
  <div className={`rently-kpi ${hero ? "is-hero" : ""}`}>
    <span>{label}</span>
    <strong className={danger ? "is-danger" : ""}>{value}</strong>
    <small>{delta}</small>
  </div>
);

const RentlyTextField = ({
  label,
  value,
  onChange,
  type = "text",
  prefix,
  suffix,
}) => (
  <div className="rently-field">
    <label>{label}</label>
    {prefix || suffix ? (
      <div className="rently-input-adornment">
        {prefix ? <span>{prefix}</span> : null}
        <input
          type={type}
          style={{ width: "100%" }}
          value={value || ""}
          onChange={(event) => onChange(event.target.value)}
        />
        {suffix ? <span>{suffix}</span> : null}
      </div>
    ) : (
      <input
        type={type}
        style={{ width: "100%" }}
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
      />
    )}
  </div>
);

const RentlyFileField = ({
  label,
  files,
  multiple = false,
  helper,
  onChange,
}) => (
  <div className="rently-field">
    <label>{label}</label>
    <input
      type="file"
      multiple={multiple}
      onChange={(event) => onChange(event.target.files)}
    />
    {helper ? <small className="rently-field-helper">{helper}</small> : null}
    {files?.length ? (
      <div className="rently-file-list">
        {files.map((file) => (
          <span key={file.id || file.name}>{file.name}</span>
        ))}
      </div>
    ) : null}
  </div>
);

const rentlyStatusClass = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (
    normalized.includes("late") ||
    normalized.includes("overdue") ||
    normalized.includes("default") ||
    normalized.includes("high") ||
    normalized.includes("120")
  ) {
    return "is-danger";
  }
  if (
    normalized.includes("processing") ||
    normalized.includes("pending") ||
    normalized.includes("medium")
  ) {
    return "is-warn";
  }
  if (
    normalized.includes("complete") ||
    normalized.includes("up to date") ||
    normalized.includes("low")
  ) {
    return "is-success";
  }
  return "is-brand";
};

const rentlyFraudBand = (item) => {
  const normalized = String(item?.risk_level || "").toLowerCase();
  const score = Number(item?.risk_score);
  if (["critical", "high"].includes(normalized) || score >= 70) {
    return "is-danger";
  }
  if (["medium", "moderate"].includes(normalized) || score >= 35) {
    return "is-warn";
  }
  return "is-success";
};

const rentlyFraudDetailValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const rentlyFraudLabel = (value) =>
  String(value || "Flag")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());

const RentlyCbsReports = ({
  cbsReports = [],
  cbsObligations = [],
  cbsMonthlyInstalmentHistories = [],
}) => (
  <section className="rently-panel rently-section-card">
    <div className="rently-panel-title">CBS Reports</div>
    {cbsReports.length === 0 ? (
      <div className="rently-empty is-compact">
        No CBS reports uploaded for this profile yet.
      </div>
    ) : (
      <div className="rently-supplementary-list">
        {cbsReports.map((report) => {
          const payload = report.parsed_payload || {};

          return (
            <div className="rently-supplementary-card" key={report.id}>
              <div className="rently-supplementary-head">
                <a href={report.document_url} target="_blank" rel="noreferrer">
                  {report.original_file_name || report.name || "CBS report"}
                </a>
                <span className={`rently-pill ${rentlyStatusClass(report.status)}`}>
                  {report.status || "-"}
                </span>
              </div>
              <div className="rently-report-metrics">
                <RentlyReportMetric
                  label="Credit Score"
                  value={payload.CreditScore ?? payload.credit_score ?? "-"}
                />
                <RentlyReportMetric
                  label="Risk Grade"
                  value={payload.RiskGrade || payload.risk_grade || "-"}
                />
                <RentlyReportMetric
                  label="Probability Default"
                  value={
                    payload.ProbabilityofDefault ??
                    payload.probability_default ??
                    "-"
                  }
                />
                <RentlyReportMetric
                  label="Unsecured Limit"
                  value={rentlyCbsMoney(
                    payload.UnsecuredCreditLimit ?? payload.unsecured_credit_limit,
                  )}
                />
                <RentlyReportMetric
                  label="Unsecured Utilized"
                  value={rentlyCbsMoney(payload.UnsecuredCreditUtilized)}
                />
                <RentlyReportMetric
                  label="Defaults"
                  value={payload.defaults ? "True" : "N/A"}
                />
              </div>
            </div>
          );
        })}
      </div>
    )}

    {cbsMonthlyInstalmentHistories.length > 0 ? (
      <div className="rently-cbs-chart-grid">
        {cbsMonthlyInstalmentHistories.map((history) => {
          const rows = Array.isArray(history.rows) ? history.rows : [];
          const maxValue = Math.max(
            ...rows.map((row) => Number(row.monthly_instalment) || 0),
            1,
          );

          return (
            <div
              className="rently-cbs-chart-card"
              key={history.id || history.report_name || "cbs-history"}
            >
              <strong>Last 6 months monthly instalment</strong>
              <span>
                {history.report_name || "CBS report"} · Enquiry{" "}
                {history.enquiry_date || "-"}
              </span>
              <div className="rently-cbs-chart">
                {rows.map((row) => {
                  const amount = Number(row.monthly_instalment) || 0;
                  const height = Math.max(10, (amount / maxValue) * 120);
                  return (
                    <div className="rently-cbs-bar-wrap" key={row.month_label}>
                      <strong>{money(amount)}</strong>
                      <i style={{ height }} />
                      <span>{row.month_label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    ) : null}

    {cbsObligations.length > 0 ? (
      <div className="rently-mini-table">
        <table>
          <thead>
            <tr>
              <th>CBS report</th>
              <th>Grantor bank</th>
              <th>Facility</th>
              <th>Outstanding</th>
              <th>Monthly instalment</th>
            </tr>
          </thead>
          <tbody>
            {cbsObligations.map((obligation) => (
              <tr key={obligation.id}>
                <td>{obligation.report_name || "CBS report"}</td>
                <td>{obligation.grantor_bank || "-"}</td>
                <td>{obligation.facility || "CBS facility"}</td>
                <td>{money(obligation.outstanding_balance)}</td>
                <td>{money(obligation.monthly_instalment)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : cbsReports.length > 0 ? (
      <div className="rently-empty is-compact">
        CBS report uploaded, but parsed obligation rows are not available yet.
      </div>
    ) : null}
  </section>
);

const RentlyMlcbReports = ({ mlcbReports = [] }) => {
  const latestReport = mlcbReports.find((report) => report.document_url);
  const rows = mlcbReports.flatMap((report) =>
    (report.entries || []).map((entry, index) => {
      const monthStatuses = entry.months ? Object.values(entry.months) : [];
      const worstStatus =
        monthStatuses.find((status) =>
          rentlyStatusClass(status).includes("danger"),
        ) ||
        monthStatuses.find((status) =>
          rentlyStatusClass(status).includes("warn"),
        ) ||
        monthStatuses[monthStatuses.length - 1] ||
        "Up to date";

      return {
        entry,
        report,
        index,
        worstStatus,
      };
    }),
  );

  return (
    <section className="rently-panel rently-section-card rently-mlcb-card">
      <div className="rently-reference-card-head">
        <div>
          <div className="rently-panel-title">Moneylender records (MLCB)</div>
          <div className="rently-panel-sub">
            Worst recent status shown · click for monthly history
          </div>
        </div>
        {latestReport?.document_url ? (
          <Button
            className="rently-download-button"
            href={latestReport.document_url}
            target="_blank"
            rel="noopener noreferrer"
            icon={<DownloadOutlined />}
          >
            Download report
          </Button>
        ) : null}
      </div>

      {mlcbReports.length === 0 ? (
        <div className="rently-empty is-compact">
          No MLCB reports uploaded for this profile yet.
        </div>
      ) : rows.length > 0 ? (
        <div className="rently-mlcb-clean-list">
          {rows.map(({ entry, report, index, worstStatus }) => (
            <details
              className="rently-mlcb-row"
              key={`${report.id}-mlcb-${index}`}
            >
              <summary>
                <div>
                  <strong>{entry.issued_by || "Issuer unavailable"}</strong>
                  <span>
                    Since {entry.date_of_loan || "-"} · payable{" "}
                    {money(entry.total_payable_amount)}
                  </span>
                </div>
                <span
                  className={`rently-pill ${rentlyStatusClass(worstStatus)}`}
                >
                  {worstStatus}
                </span>
              </summary>
              {entry.months ? (
                <div className="rently-mlcb-months">
                  {Object.entries(entry.months)
                    .sort(([left], [right]) => left.localeCompare(right))
                    .map(([month, status]) => (
                      <i key={`${report.id}-${index}-${month}`}>
                        {month}: {status}
                      </i>
                    ))}
                </div>
              ) : null}
            </details>
          ))}
        </div>
      ) : mlcbReports.some((report) => report.status === "processing") ? (
        <div className="rently-processing-note">
          OCR is still running for this MLCB report. This section will update
          when analysis is ready.
        </div>
      ) : (
        <div className="rently-empty is-compact">
          MLCB report uploaded, but parsed repayment entries are not available
          yet.
        </div>
      )}
    </section>
  );
};

const RentlyDocumentFraudChecks = ({ fraudChecks = [] }) => (
  <section className="rently-panel rently-section-card">
    <div className="rently-documents-heading">
      <div>
        <div className="rently-panel-title">Document fraud checks</div>
        <div className="rently-panel-sub">
          0 = low risk, 100 = high risk. Expand a statement for full findings.
        </div>
      </div>
      {fraudChecks.length > 0 ? (
        <span
          className={`rently-pill ${rentlyFraudBand(
            fraudChecks.reduce((highest, item) =>
              Number(item?.risk_score) > Number(highest?.risk_score || 0)
                ? item
                : highest,
            ),
          )}`}
        >
          Highest score:{" "}
          {Math.max(...fraudChecks.map((item) => Number(item.risk_score) || 0))}
          /100
        </span>
      ) : null}
    </div>
    {fraudChecks.length === 0 ? (
      <div className="rently-empty is-compact">
        No document fraud checks are available for these bank statements yet.
      </div>
    ) : (
      <div className="rently-fraud-list">
        {fraudChecks.map((item) => {
          const score = Number(item.risk_score);
          const boundedScore = Number.isFinite(score)
            ? Math.max(0, Math.min(score, 100))
            : 0;
          const flags = Array.isArray(item.flags) ? item.flags : [];
          const firstFlag = flags[0];
          const band = rentlyFraudBand(item);

          return (
            <details
              className="rently-fraud-row"
              key={`${item.job_id || item.file_name || "fraud"}-${item.account_number || ""}`}
            >
              <summary>
                <div className="rently-fraud-summary">
                  <div>
                    <strong>
                      {item.file_name || item.bank || "Bank statement"}
                    </strong>
                    <span>
                      {item.bank || "Unknown Bank"} ·{" "}
                      {item.account_number || "Unknown Account"} ·{" "}
                      {item.month_label || "-"}
                    </span>
                  </div>
                  <span className={`rently-pill ${band}`}>
                    {item.risk_level || "Risk"} · {boundedScore}/100
                  </span>
                </div>
                <div className="rently-fraud-bar">
                  <i className={band} style={{ width: `${boundedScore}%` }} />
                </div>
                <p>
                  {firstFlag?.message ||
                    (flags.length
                      ? `${flags.length} fraud flag(s) detected`
                      : "No fraud flags detected for this statement.")}
                </p>
              </summary>
              <div className="rently-fraud-details">
                {flags.length > 0 ? (
                  flags.map((flag, index) => (
                    <div
                      className="rently-fraud-flag"
                      key={`${flag.code || "flag"}-${index}`}
                    >
                      <div className="rently-fraud-summary">
                        <strong>{rentlyFraudLabel(flag.code)}</strong>
                        <span
                          className={`rently-pill ${rentlyStatusClass(
                            flag.severity,
                          )}`}
                        >
                          {flag.severity || "medium"}
                        </span>
                      </div>
                      <p>{flag.message || "Fraud check flag detected."}</p>
                      {flag.details && Object.keys(flag.details).length > 0 ? (
                        <dl>
                          {Object.entries(flag.details).map(([key, value]) => (
                            <React.Fragment key={key}>
                              <dt>{rentlyFraudLabel(key)}</dt>
                              <dd>{rentlyFraudDetailValue(value)}</dd>
                            </React.Fragment>
                          ))}
                        </dl>
                      ) : null}
                    </div>
                  ))
                ) : (
                  <div className="rently-empty is-compact">
                    No fraud flags detected for this statement.
                  </div>
                )}
              </div>
            </details>
          );
        })}
      </div>
    )}
  </section>
);

const RentlyReportMetric = ({ label, value }) => (
  <div>
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const normalizeSccbReportData = (report) => {
  const parsed = report?.parsed_report;
  if (!parsed || typeof parsed !== "object") return {};
  return parsed.REPORT || parsed.RESPONSE?.REPORT || parsed;
};

const sccbValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (Array.isArray(value))
    return value.length ? `${value.length} item(s)` : "-";
  if (typeof value === "object")
    return Object.keys(value).length ? "Available" : "-";
  return String(value);
};

const sccbToArray = (value) =>
  Array.isArray(value) ? value : value ? [value] : [];

const sccbJoin = (parts, separator = " | ") =>
  parts
    .map((part) => sccbValue(part))
    .filter((part) => part && part !== "-")
    .join(separator) || "-";

const SccbKeyValueSection = ({ title, data }) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return (
      <div className="rently-sccb-section">
        <h4>{title}</h4>
        <span>{sccbValue(data)}</span>
      </div>
    );
  }

  return (
    <div className="rently-sccb-section">
      <h4>{title}</h4>
      <dl className="rently-sccb-kv">
        {Object.entries(data).map(([key, value]) => (
          <React.Fragment key={key}>
            <dt>{key.replace(/_/g, " ")}</dt>
            <dd>{sccbValue(value)}</dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
};

const SccbTableSection = ({ title, columns, rows }) => {
  const safeRows = rows?.length ? rows : [];

  return (
    <div className="rently-sccb-section">
      <h4>{title}</h4>
      <div className="rently-sccb-table-wrap">
        <table className="rently-sccb-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column}>{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {safeRows.map((row, index) => (
              <tr key={`${title}-${index}`}>
                {columns.map((column) => (
                  <td key={`${title}-${index}-${column}`}>
                    {sccbValue(row[column])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const buildInlineSccbTables = (data = {}) => {
  const gst = data.GST || {};
  const capital = sccbToArray(data.LIST_CAPITAL?.CAPITAL);
  const officers = sccbToArray(data.LIST_OFFICER_OWNER?.OFFICER_OWNER);
  const shareholders = sccbToArray(data.LIST_SHAREHOLDER?.SHAREHOLDER);
  const charges = sccbToArray(data.LIST_REG_CHARGES?.REG_CHARGES);
  const litigation = sccbToArray(data.LIST_LITIGATION?.LITIGATION);
  const windingUp = sccbToArray(data.LIST_WINDING_UP?.WINDING_UP);

  return [
    {
      title: "GST",
      columns: [
        "GST REGISTRATION NUMBER",
        "GST REGISTERED FROM",
        "GST REGISTERED TO",
        "GST STATUS",
      ],
      rows: [
        {
          "GST REGISTRATION NUMBER": sccbValue(gst.GST_REGISTRATION_NUMBER),
          "GST REGISTERED FROM": sccbValue(gst.GST_REGISTERED_FROM),
          "GST REGISTERED TO": sccbValue(gst.GST_REGISTERED_TO),
          "GST STATUS": sccbValue(gst.GST_STATUS),
        },
      ],
    },
    {
      title: "Capital",
      columns: [
        "CAPITAL CATEGORY",
        "CURRENCY",
        "CAPITAL AMOUNT",
        "NO. OF SHARES",
      ],
      rows: capital.length
        ? capital.map((row) => ({
            "CAPITAL CATEGORY": row.CAPITAL_CATEGORY,
            CURRENCY: row.CURRENCY,
            "CAPITAL AMOUNT": row.CAPITAL_AMT,
            "NO. OF SHARES": row.NO_OF_SHARES,
          }))
        : [
            {
              "CAPITAL CATEGORY": "-",
              CURRENCY: "-",
              "CAPITAL AMOUNT": "-",
              "NO. OF SHARES": "-",
            },
          ],
    },
    {
      title: "Officer and Owners",
      columns: [
        "OFFICER NAME / ADDRESS / CHANGE ADDRESS DATE",
        "IDENTITY NO. / PA REG. NO.",
        "POSITION",
        "APPOINTMENT DATE / DISQUALIFIED DATE",
        "NATIONALITY",
      ],
      rows: officers.length
        ? officers.map((row) => ({
            "OFFICER NAME / ADDRESS / CHANGE ADDRESS DATE": sccbJoin([
              row.OFFICER_NAME,
              row.ADDRESS,
              row.CHANGE_ADDR_DATE,
            ]),
            "IDENTITY NO. / PA REG. NO.": sccbJoin(
              [row.IDENTITY_ID, row.PA_REG_NO],
              " ",
            ),
            POSITION: row.POSITION,
            "APPOINTMENT DATE / DISQUALIFIED DATE": sccbJoin(
              [row.APPMT_DATE, row.DISQUALIFIED_DATE],
              " / ",
            ),
            NATIONALITY: row.NATIONALITY,
          }))
        : [
            {
              "OFFICER NAME / ADDRESS / CHANGE ADDRESS DATE": "-",
              "IDENTITY NO. / PA REG. NO.": "-",
              POSITION: "-",
              "APPOINTMENT DATE / DISQUALIFIED DATE": "-",
              NATIONALITY: "-",
            },
          ],
    },
    {
      title: "Shareholders",
      columns: [
        "SHAREHOLDER NAME / ADDRESS / CHANGE ADDRESS DATE",
        "COMPANY / IDENTITY NO.",
        "COUNTRY OF INCORPORATION",
        "SHARE TYPE",
        "CURRENCY",
        "NOS. OF SHARES",
        "SHARE GROUP",
      ],
      rows: shareholders.length
        ? shareholders.map((row) => ({
            "SHAREHOLDER NAME / ADDRESS / CHANGE ADDRESS DATE": sccbJoin([
              row.SHAREHOLDER_NAME,
              row.ADDRESS,
              row.CHANGE_ADDR_DATE,
            ]),
            "COMPANY / IDENTITY NO.": row.COMP_ID,
            "COUNTRY OF INCORPORATION": row.NATIONALITY,
            "SHARE TYPE": row.SHARE_TYPE,
            CURRENCY: row.CURRENCY,
            "NOS. OF SHARES": row.NO_OF_SHARE,
            "SHARE GROUP": row.SHARE_GROUP,
          }))
        : [
            {
              "SHAREHOLDER NAME / ADDRESS / CHANGE ADDRESS DATE": "-",
              "COMPANY / IDENTITY NO.": "-",
              "COUNTRY OF INCORPORATION": "-",
              "SHARE TYPE": "-",
              CURRENCY: "-",
              "NOS. OF SHARES": "-",
              "SHARE GROUP": "-",
            },
          ],
    },
    {
      title: "Registered Charges",
      columns: [
        "CHARGE NO",
        "CHARGE DATE",
        "CHARGEE(S) COMPANY",
        "CURRENCY",
        "AMOUNT SECURED",
        "STATUS OF SATISFACTION",
      ],
      rows: charges.length
        ? charges.map((row) => ({
            "CHARGE NO": row.CHARGE_NO,
            "CHARGE DATE": row.CHARGE_DATE,
            "CHARGEE(S) COMPANY": row.CHARGEE_COMP,
            CURRENCY: row.CURRENCY,
            "AMOUNT SECURED": row.AMT_SECURED,
            "STATUS OF SATISFACTION": row.STATUS_SATISFACTION,
          }))
        : [
            {
              "CHARGE NO": "Nil",
              "CHARGE DATE": "-",
              "CHARGEE(S) COMPANY": "-",
              CURRENCY: "-",
              "AMOUNT SECURED": "-",
              "STATUS OF SATISFACTION": "-",
            },
          ],
    },
    {
      title: "Litigation",
      columns: [
        "CASE NO.",
        "DATE OF WRIT",
        "STATUS / STATUS DATE",
        "NATURE OF CLAIM / AMOUNT",
        "DEFENDANT STATUS",
        "PLAINTIFF / CLAIMANT STATUS",
      ],
      rows: litigation.length
        ? litigation.map((row) => ({
            "CASE NO.": sccbJoin(
              [row.COURT_CODE, row.CASE_YEAR, row.CASE_TYPE],
              " ",
            ),
            "DATE OF WRIT": row.DATE_OF_WRIT,
            "STATUS / STATUS DATE": sccbJoin([
              row.CASE_STATUS,
              row.CASE_STATUS_DATE,
            ]),
            "NATURE OF CLAIM / AMOUNT": sccbJoin([
              row.NATURE_OF_CLAIM,
              sccbJoin([row.CURRENCY, row.CLAIM_AMOUNT], " "),
            ]),
            "DEFENDANT STATUS": sccbJoin([
              row.DEF_STATUS,
              row.DEF_STATUS_DATE,
              row.DEF_SOLICITOR,
              row.DEF_LAW_FIRM,
              row.DEF_FILE_REF,
            ]),
            "PLAINTIFF / CLAIMANT STATUS": sccbJoin([
              row.PLA_STATUS,
              row.PLA_STATUS_DATE,
              row.PLAINTIFF,
              row.PLA_SOLICITOR,
              row.PLA_LAW_FIRM,
              row.PLA_FILE_REF,
            ]),
          }))
        : [{ "CASE NO.": "NO RECORD FOUND" }],
    },
    {
      title: "Winding Up",
      columns: ["CASE NO. / TYPE", "ACTION DATE", "AMOUNT", "PLAINTIFF"],
      rows: windingUp.length
        ? windingUp.map((row) => ({
            "CASE NO. / TYPE": sccbJoin(
              [row.COURT_CODE, row.CASE_YEAR, row.CASE_TYPE],
              " ",
            ),
            "ACTION DATE": row.ACTION_DATE,
            AMOUNT: sccbJoin([row.CURRENCY1, row.AMOUNT1 || "0.00"], " "),
            PLAINTIFF: row.PLAINTIFF,
          }))
        : [
            {
              "CASE NO. / TYPE": "NO RECORD FOUND",
              "ACTION DATE": "-",
              AMOUNT: "-",
              PLAINTIFF: "-",
            },
          ],
    },
  ];
};

const RentlySccbReports = ({
  reports = [],
  profile,
  retrievingSccb,
  onRetrieveSccb,
  onOpenSccbReport,
}) => {
  const canRetrieve =
    Boolean(textValue(profile?.companyName || profile?.company_name).trim()) &&
    Boolean(textValue(profile?.uen).trim());
  const latestReport =
    reports.find((report) => report.status === "completed") || reports[0];
  const data = normalizeSccbReportData(latestReport);
  const registry = data.REGISTRY_DETAIL || {};
  const inlineTables = buildInlineSccbTables(data);

  return (
    <section className="rently-panel rently-sccb-panel">
      <div className="rently-documents-heading">
        <div>
          <div className="rently-panel-title">SCCB Biz Check Plus</div>
          <div className="rently-panel-sub">
            Uses 20 tokens per retrieval. Latest completed report is cached
            here.
          </div>
        </div>
        <Space className="rently-profile-actions">
          {latestReport ? (
            <Button size="small" onClick={() => onOpenSccbReport(latestReport)}>
              Open Document
            </Button>
          ) : null}
          <Button
            size="small"
            type="primary"
            loading={retrievingSccb}
            disabled={!canRetrieve}
            onClick={onRetrieveSccb}
          >
            Retrieve SCCB
          </Button>
        </Space>
      </div>
      {!canRetrieve ? (
        <div className="rently-empty is-compact">
          Add company name and UEN to the client profile before retrieval.
        </div>
      ) : !latestReport ? (
        <div className="rently-empty is-compact">
          No SCCB Biz Check Plus report retrieved for this profile yet.
        </div>
      ) : latestReport.status !== "completed" ? (
        <div className="rently-empty is-compact">
          Latest SCCB retrieval failed:{" "}
          {latestReport.error || "No error details available."}
        </div>
      ) : (
        <>
          <div className="rently-report-subline">
            Retrieved{" "}
            {latestReport.retrieved_at
              ? new Date(latestReport.retrieved_at).toLocaleString()
              : "-"}{" "}
            ·{latestReport.tokens_consumed || 0} tokens
          </div>
          <SccbKeyValueSection
            title="Company"
            data={{
              "Company Name": registry?.COMP_NAME || latestReport.company_name,
              "Registration No": registry?.REG_NO || latestReport.uen,
            }}
          />
          <SccbKeyValueSection title="Registry Details" data={registry} />
          {inlineTables.map((table) => (
            <SccbTableSection
              key={table.title}
              title={table.title}
              columns={table.columns}
              rows={table.rows}
            />
          ))}
        </>
      )}
    </section>
  );
};

const RentlyCaseDetail = ({
  caseItem,
  profile,
  profileReport,
  profileReportLoading,
  savingProfileReportScenario,
  onProfileReportScenarioChange,
  onEditProfile,
  onUploadMoreStatements,
  onRetrieveSccb,
  onOpenSccbReport,
  retrievingSccb,
  onDeleteBankStatement,
  onCreateCase,
  onEditCase,
  onDeleteCase,
  onMarkPaid,
  onAdjustReceivedDate,
  remarkText,
  setRemarkText,
  onAddRemark,
}) => {
  const repayments = Array.isArray(caseItem?.repayments)
    ? caseItem.repayments
    : [];
  const documents = profile?.documents || {};
  const bankStatementReports =
    profileReport?.submissions || documents.bankStatements || [];
  const hasReport = Boolean(profileReport?.report);
  const cbsReports = profileReport?.cbs_reports || documents.cbsReport || [];
  const mlcbReports = profileReport?.mlcb_reports || documents.mlcbReport || [];
  const sccbReports =
    profileReport?.sccb_reports ||
    profile?.sccb_reports ||
    documents.sccbReports ||
    [];
  const cbsObligations = getCbsObligationRows(cbsReports);
  const cbsMonthlyInstalmentHistories =
    getCbsMonthlyInstalmentHistories(cbsReports);
  const groupedAnalysisStatements = groupAnalysisStatements(
    profileReport?.statements || [],
  );
  const fraudChecks = Array.isArray(profileReport?.report?.fraud_checks)
    ? profileReport.report.fraud_checks
    : [];
  const sectionLinks = [
    { id: "case", label: "Case" },
    { id: "sccb", label: "SCCB" },
    { id: "bank", label: "Bank st." },
    { id: "cbs", label: "CBS" },
    { id: "mlcb", label: "MLCB" },
  ];
  const scrollToCaseSection = (sectionId) => {
    const element = document.getElementById(`rently-section-${sectionId}`);
    element?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="rently-detail rently-case-file">
      <div className="rently-detail-header">
        <div>
          <h2>{profile?.name || "Selected Case"}</h2>
          <p>
            {profile?.companyName ||
              profile?.email ||
              "No client profile details"}
          </p>
        </div>
        <Space className="rently-profile-actions">
          <Button onClick={onEditProfile}>Edit Profile</Button>
          <Button type="primary" onClick={onCreateCase}>
            Create Case
          </Button>
          {caseItem ? <Button onClick={onEditCase}>Edit Case</Button> : null}
          {caseItem ? (
            <Button danger onClick={onDeleteCase}>
              Delete
            </Button>
          ) : null}
        </Space>
      </div>

      <div className="rently-case-file-layout">
        <main className="rently-case-file-main">
          <section
            id="rently-section-case"
            className="rently-section-block"
          >
            <div className="rently-section-heading">
              <p>Case details</p>
              <h3>Client profile and case trail</h3>
            </div>
            <div className="rently-detail-grid">
              <div className="rently-panel">
                <div className="rently-panel-title">Client Profile</div>
                <dl className="rently-definition-list">
                  <dt>Contact</dt>
                  <dd>{profile?.contact || "-"}</dd>
                  <dt>Email</dt>
                  <dd>{profile?.email || "-"}</dd>
                  <dt>UEN / ID</dt>
                  <dd>{profile?.uen || "-"}</dd>
                  <dt>CBS report</dt>
                  <dd>
                    {documents.cbsReport?.[0]?.name ||
                      documents.cbsReport?.[0]?.original_file_name ||
                      "-"}
                  </dd>
                  <dt>MLCB report</dt>
                  <dd>
                    {documents.mlcbReport?.[0]?.name ||
                      documents.mlcbReport?.[0]?.original_file_name ||
                      "-"}
                  </dd>
                  <dt>SCCB report</dt>
                  <dd>
                    {sccbReports?.[0]?.status === "completed"
                      ? "Retrieved"
                      : "-"}
                  </dd>
                </dl>
              </div>

              {caseItem ? (
                <div className="rently-panel">
                  <div className="rently-panel-title">Case Terms</div>
                  <dl className="rently-definition-list">
                    <dt>Loan Amount</dt>
                    <dd>{money(caseItem.loanAmount)}</dd>
                    <dt>Inception</dt>
                    <dd>{formatDate(caseItem.inceptionDate)}</dd>
                    <dt>Tenor</dt>
                    <dd>{rentlyTenorMonths(caseItem)}</dd>
                    <dt>Interest</dt>
                    <dd>
                      {numberValue(caseItem.interestPercentage)}% ·{" "}
                      {RENTLY_INTEREST_BASIS_OPTIONS.find(
                        (option) =>
                          option.value ===
                          rentlyNormalizeInterestBasis(caseItem.interestBasis),
                      )?.label || "P2P base interest"}
                    </dd>
                    <dt>Processing Fee</dt>
                    <dd>
                      {caseItem.processingFeeEnabled
                        ? money(
                            rentlyFeeAmount(
                              true,
                              caseItem.processingFeeMode,
                              caseItem.processingFeeValue,
                              numberValue(caseItem.loanAmount),
                            ),
                          )
                        : "No"}
                    </dd>
                    <dt>Disbursement</dt>
                    <dd>{money(rentlyDisbursementAmount(caseItem))}</dd>
                    <dt>Referral Profit</dt>
                    <dd>
                      {caseItem.referralProfitEnabled
                        ? money(
                            rentlyFeeAmount(
                              true,
                              caseItem.referralProfitMode,
                              caseItem.referralProfitValue,
                              numberValue(caseItem.loanAmount),
                            ),
                          )
                        : "No"}
                    </dd>
                    <dt>Banker</dt>
                    <dd>
                      {[caseItem.bankerName, caseItem.bankerContact]
                        .filter(Boolean)
                        .join(" · ") || "-"}
                    </dd>
                  </dl>
                </div>
              ) : null}

              <div className="rently-panel">
                <div className="rently-panel-title">Case Trail</div>
                {caseItem ? (
                  <div className="rently-trail">
                    {(caseItem.trail || [])
                      .slice()
                      .reverse()
                      .map((trail, index, allTrail) => (
                        <div
                          className="rently-trail-item"
                          key={trail.id || `${trail.action}-${trail.at}`}
                        >
                          <span className="rently-trail-marker">
                            <i
                              className={
                                index === 0 ? "is-current" : "is-muted"
                              }
                            />
                            {index === allTrail.length - 1 ? null : <b />}
                          </span>
                          <span className="rently-trail-copy">
                            <strong>{trail.action}</strong>
                            <span>{new Date(trail.at).toLocaleString()}</span>
                          </span>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="rently-empty is-compact">
                    No case trail yet. Create a case from this profile to start
                    tracking activity.
                  </div>
                )}
              </div>

              {caseItem ? (
                <div className="rently-panel">
                  <div className="rently-panel-title">Repayments</div>
                  {repayments.length === 0 ? (
                    <div className="rently-empty">
                      No repayment table generated yet.
                    </div>
                  ) : (
                    <div className="rently-mini-table">
                      <table>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Due</th>
                            <th>Principal</th>
                            <th>Interest</th>
                            <th>Total</th>
                            <th>Received</th>
                            <th>Information</th>
                            <th>Paid</th>
                          </tr>
                        </thead>
                        <tbody>
                          {repayments.map((repayment) => (
                            <tr
                              className={
                                !repayment.paid &&
                                rentlyDaysBetween(
                                  repayment.dueDate,
                                  rentlyToday(),
                                ) > 0
                                  ? "is-overdue"
                                  : ""
                              }
                              key={repayment.id}
                            >
                              <td>{repayment.period}</td>
                              <td>{formatDate(repayment.dueDate)}</td>
                              <td>{money(repayment.amount)}</td>
                              <td>{money(repayment.interestEarned)}</td>
                              <td>{money(rentlyTotalRepayment(repayment))}</td>
                              <td>
                                <input
                                  type="date"
                                  value={repayment.receivedDate || ""}
                                  onChange={(event) =>
                                    onAdjustReceivedDate(
                                      caseItem.id,
                                      repayment.id,
                                      event.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td>{rentlyRepaymentStatus(repayment)}</td>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={Boolean(repayment.paid)}
                                  onChange={(event) =>
                                    onMarkPaid(
                                      caseItem.id,
                                      repayment.id,
                                      event.target.checked,
                                    )
                                  }
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : null}

              {caseItem ? (
                <div className="rently-panel">
                  <div className="rently-panel-title">Remarks</div>
                  <div className="rently-remark-box">
                    <Input.TextArea
                      rows={3}
                      value={remarkText}
                      onChange={(event) => setRemarkText(event.target.value)}
                      placeholder="Add a case remark..."
                    />
                    <Button type="primary" onClick={onAddRemark}>
                      Add Remark
                    </Button>
                  </div>
                  <div className="rently-remarks">
                    {(caseItem.remarks || [])
                      .slice()
                      .reverse()
                      .map((remark) => (
                        <div key={remark.id}>
                          <p>{remark.text}</p>
                          <span>
                            {remark.author || "User"} -{" "}
                            {new Date(remark.at).toLocaleString()}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              ) : null}
            </div>
          </section>

          <section id="rently-section-sccb" className="rently-section-block">
            <div className="rently-section-heading">
              <p>SCCB details</p>
              <h3>Biz Check Plus report</h3>
            </div>
            <RentlySccbReports
              reports={sccbReports}
              profile={profile}
              retrievingSccb={retrievingSccb}
              onRetrieveSccb={onRetrieveSccb}
              onOpenSccbReport={onOpenSccbReport}
            />
          </section>

          <section id="rently-section-bank" className="rently-section-block">
            <div className="rently-panel rently-bank-card">
              <div className="rently-reference-card-head">
                <div className="rently-panel-title">Bank statements</div>
                <Button
                  className="rently-upload-more-button"
                  onClick={onUploadMoreStatements}
                >
                  Upload more
                </Button>
              </div>
              {bankStatementReports.length > 0 ? (
                <div className="rently-bank-statement-list">
                  {bankStatementReports.map((file) => (
                    <div className="rently-bank-statement-item" key={file.id}>
                      <div>
                        <strong>
                          {file.name ||
                            file.original_file_name ||
                            "Bank statement"}
                        </strong>
                        <span>
                          {file.status || "queued"}
                          {file.tokens_consumed
                            ? ` · ${file.tokens_consumed} tokens`
                            : ""}
                        </span>
                      </div>
                      <div className="rently-bank-statement-actions">
                        <span
                          className={`rently-pill ${rentlyStatusClass(
                            file.status,
                          )}`}
                        >
                          {file.status || "queued"}
                        </span>
                        <Button
                          className="rently-download-button"
                          href={file.document_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          disabled={!file.document_url}
                          icon={<DownloadOutlined />}
                        >
                          Download
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rently-empty is-compact">
                  No bank statements uploaded.
                </div>
              )}
            </div>

            <RentlyDocumentFraudChecks fraudChecks={fraudChecks} />

            {profileReportLoading ? (
              <div className="rently-panel">
                <Spin /> Loading lending report...
              </div>
            ) : hasReport ? (
              <div className="rently-report-panel">
                <BankStatementGroupReport
                  report={profileReport.report}
                  groupName={profileReport.group?.name || profile?.name}
                  cbsObligations={cbsObligations}
                  cbsMonthlyInstalmentHistories={
                    cbsMonthlyInstalmentHistories
                  }
                  hideFraudChecks
                  hideCbsSection
                  savedScenario={profileReport.group?.report_scenario}
                  onScenarioChange={onProfileReportScenarioChange}
                  savingScenario={savingProfileReportScenario}
                />
                <div className="rently-panel rently-combined-analysis">
                  <div className="rently-panel-title">Combined analysis</div>
                  <div className="rently-panel-sub">
                    Parsed statements from every completed file in this group
                    are merged here for detailed review alongside the credit
                    memo.
                  </div>
                  <BankStatementAnalysisPanel
                    enabled={true}
                    loading={profileReportLoading}
                    error={null}
                    statementGroups={groupedAnalysisStatements}
                    isMobile={false}
                    formatAmount={money}
                    formatDate={formatDate}
                    emptyMessage="Analysis is not ready yet for this profile."
                  />
                </div>
              </div>
            ) : (
              <div className="rently-panel">
                <div className="rently-panel-title">Lending Report</div>
                <div className="rently-empty is-compact">
                  Upload bank statements and wait for processing to complete
                  before the lending report appears here.
                </div>
              </div>
            )}
          </section>

          <section id="rently-section-cbs" className="rently-section-block">
            <div className="rently-section-heading">
              <p>CBS details</p>
              <h3>Credit bureau report</h3>
            </div>
            <RentlyCbsReports
              cbsReports={cbsReports}
              cbsObligations={cbsObligations}
              cbsMonthlyInstalmentHistories={cbsMonthlyInstalmentHistories}
            />
          </section>

          <section id="rently-section-mlcb" className="rently-section-block">
            <RentlyMlcbReports mlcbReports={mlcbReports} />
          </section>
        </main>
        <aside className="rently-section-rail" aria-label="Case sections">
          {sectionLinks.map((link) => (
            <button
              type="button"
              key={link.id}
              onClick={() => scrollToCaseSection(link.id)}
            >
              <span />
              {link.label}
            </button>
          ))}
        </aside>
      </div>
    </section>
  );
};

const ManageCases = () => {
  const userEmail = Cookies.get("email");
  const [manageCasesVariant, setManageCasesVariant] = useState(
    isRentlyEmail(userEmail) ? "rently" : "",
  );
  const [checkingVariant, setCheckingVariant] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const checkManageCasesVariant = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/lender/rently/manage-cases/access`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${Cookies.get("token")}`,
            },
          },
        );
        const json = await response.json();
        if (
          !cancelled &&
          response.ok &&
          json.enabled &&
          json.variant === "rently"
        ) {
          setManageCasesVariant("rently");
        }
      } catch (error) {
        if (!cancelled && isRentlyEmail(userEmail)) {
          setManageCasesVariant("rently");
        }
      } finally {
        if (!cancelled) {
          setCheckingVariant(false);
        }
      }
    };

    checkManageCasesVariant();

    return () => {
      cancelled = true;
    };
  }, [userEmail]);

  if (checkingVariant) {
    return (
      <div className="manage-cases-page">
        <Spin /> Loading Manage Cases...
      </div>
    );
  }

  return manageCasesVariant === "rently" ? (
    <RentlyManageCases />
  ) : (
    <SmartLendManageCases />
  );
};

const SummaryCard = ({ label, value }) => (
  <div className="manage-cases-summary-card">
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const CaseForm = ({
  draft,
  updateDraft,
  borrowerSuggestions,
  loanRefSuggestions,
  funders,
  selectedFunderIds,
  selectedFunders,
  setSelectedFunders,
  updateExposure,
  openCreditModal,
}) => (
  <>
    <div className="manage-cases-form-grid">
      <div className="manage-cases-field">
        <label>Borrower name *</label>
        <input
          value={draft.borrower_name}
          onChange={(event) => updateDraft("borrower_name", event.target.value)}
        />
        {borrowerSuggestions.length > 0 ? (
          <div className="manage-cases-suggestions">
            {borrowerSuggestions.map((item) => (
              <button
                className="manage-cases-suggestion"
                key={`${item.borrower_name}-${item.borrower_uen}`}
                type="button"
                onClick={() => {
                  updateDraft("borrower_name", item.borrower_name || "");
                  updateDraft("borrower_uen", item.borrower_uen || "");
                }}
              >
                {item.borrower_name}{" "}
                {item.borrower_uen ? `(${item.borrower_uen})` : ""}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <div className="manage-cases-field">
        <label>Borrower UEN</label>
        <input
          value={draft.borrower_uen || ""}
          onChange={(event) => updateDraft("borrower_uen", event.target.value)}
        />
      </div>
      <div className="manage-cases-field">
        <label>Loan ref</label>
        <input
          value={draft.loan_ref || ""}
          onChange={(event) => updateDraft("loan_ref", event.target.value)}
        />
        {loanRefSuggestions.length > 0 ? (
          <div className="manage-cases-suggestions">
            {loanRefSuggestions.map((loanRef) => (
              <button
                className="manage-cases-suggestion"
                key={loanRef}
                type="button"
                onClick={() => updateDraft("loan_ref", loanRef)}
              >
                {loanRef}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <div className="manage-cases-field">
        <label>Credit grade</label>
        <button
          className="manage-cases-credit-button"
          type="button"
          onClick={openCreditModal}
        >
          <span>{draft.credit_grade || "Set credit grade"}</span>
          <CalculatorOutlined />
        </button>
        {draft.credit_assessment?.result ? (
          <div className="manage-cases-credit-mini">
            Score {formatScore(draft.credit_assessment.result.finalScore)} -{" "}
            {draft.credit_assessment.result.finalDecision || "-"} -{" "}
            {draft.credit_assessment.result.hardRuleBreaches || 0} breach
            {draft.credit_assessment.result.hardRuleBreaches === 1 ? "" : "es"}
          </div>
        ) : null}
      </div>
      <div className="manage-cases-field">
        <label>Approved amount</label>
        <input
          type="number"
          min="0"
          value={draft.approved_amount ?? ""}
          onChange={(event) =>
            updateDraft("approved_amount", event.target.value)
          }
        />
      </div>
      <div className="manage-cases-field">
        <label>Tenor</label>
        <div className="manage-cases-row">
          <input
            type="number"
            min="0"
            placeholder="Months"
            value={draft.tenor_months ?? ""}
            onChange={(event) =>
              updateDraft("tenor_months", event.target.value)
            }
          />
          <input
            type="number"
            min="0"
            placeholder="Days"
            value={draft.tenor_days ?? ""}
            onChange={(event) => updateDraft("tenor_days", event.target.value)}
          />
        </div>
      </div>
      <div className="manage-cases-field">
        <label>Loan type</label>
        <select
          value={draft.loan_type || ""}
          onChange={(event) => updateDraft("loan_type", event.target.value)}
        >
          <option value="">Select</option>
          {LOAN_TYPES.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
      </div>
      <div className="manage-cases-field">
        <label>Loan terms</label>
        <select
          value={draft.loan_terms || ""}
          onChange={(event) => updateDraft("loan_terms", event.target.value)}
        >
          <option value="">Select</option>
          {LOAN_TERMS.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
      </div>
      <div className="manage-cases-field">
        <label>Interest per month (%)</label>
        <input
          type="number"
          min="0"
          step="0.01"
          value={draft.interest_per_month ?? ""}
          onChange={(event) =>
            updateDraft("interest_per_month", event.target.value)
          }
        />
      </div>
      <div className="manage-cases-field">
        <label>Processing fee (%)</label>
        <input
          type="number"
          min="0"
          step="0.01"
          value={draft.processing_fee_percentage ?? ""}
          onChange={(event) =>
            updateDraft("processing_fee_percentage", event.target.value)
          }
        />
      </div>
      <div className="manage-cases-field">
        <label>Disbursed date</label>
        <input
          type="date"
          value={draft.disbursed_date || ""}
          onChange={(event) =>
            updateDraft("disbursed_date", event.target.value)
          }
        />
      </div>
      <div className="manage-cases-field">
        <label>Funders</label>
        <Select
          mode="multiple"
          allowClear
          value={selectedFunderIds}
          placeholder="Select funders"
          onChange={setSelectedFunders}
          options={funders.map((item) => ({
            label: item.name,
            value: String(item.id),
          }))}
        />
      </div>
    </div>

    {selectedFunders.length > 0 ? (
      <>
        <div className="manage-cases-divider" />
        <div className="manage-cases-exposures">
          {selectedFunders.map((funder) => {
            const exposure = draft.funder_exposures.find(
              (item) => String(item.funder_id) === String(funder.id),
            );
            return (
              <div className="manage-cases-exposure-card" key={funder.id}>
                <h3>{funder.name}</h3>
                <div className="manage-cases-field">
                  <label>Funder exposure</label>
                  <input
                    type="number"
                    min="0"
                    value={exposure?.exposure_amount ?? ""}
                    onChange={(event) =>
                      updateExposure(funder.id, event.target.value)
                    }
                  />
                </div>
                <div className="manage-cases-table-small">
                  Cost of fund: {funder.cost_of_fund_rate || 0}% per month
                </div>
              </div>
            );
          })}
        </div>
      </>
    ) : null}
  </>
);

const CreditAssessmentModal = ({
  open,
  inputs,
  result,
  onCancel,
  onApply,
  onChange,
}) => (
  <Modal
    className="manage-cases-credit-modal"
    open={open}
    title="SME Credit Risk Assessment"
    width={1180}
    onCancel={onCancel}
    footer={[
      <Button key="cancel" onClick={onCancel}>
        Cancel
      </Button>,
      <Button key="apply" type="primary" onClick={onApply}>
        Apply Grade {result.grade}
      </Button>,
    ]}
  >
    <div className="manage-cases-credit-layout">
      <div className="manage-cases-credit-inputs">
        {CREDIT_FIELD_SECTIONS.map((section) => (
          <section className="manage-cases-credit-section" key={section.title}>
            <h3>{section.title}</h3>
            <div className="manage-cases-credit-field-grid">
              {section.fields.map((field) => (
                <div className="manage-cases-field" key={field.key}>
                  <label>
                    {field.label}
                    {field.recordOnly ? (
                      <span className="manage-cases-credit-tag">
                        record only
                      </span>
                    ) : null}
                  </label>
                  {field.type === "select" ? (
                    <select
                      value={inputs[field.key] || ""}
                      onChange={(event) =>
                        onChange(field.key, event.target.value)
                      }
                    >
                      <option value="">Select</option>
                      {field.options.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : field.type === "number" ? (
                    <input
                      type="number"
                      min={field.min}
                      max={field.max}
                      step={field.step || "1"}
                      value={inputs[field.key] ?? ""}
                      onChange={(event) =>
                        onChange(field.key, event.target.value)
                      }
                    />
                  ) : (
                    <input
                      type="text"
                      value={inputs[field.key] ?? ""}
                      onChange={(event) =>
                        onChange(field.key, event.target.value)
                      }
                    />
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      <aside className="manage-cases-credit-results">
        <div className="manage-cases-credit-score">
          <span>Credit Grade</span>
          <strong>{result.grade}</strong>
        </div>
        <div className="manage-cases-credit-kpis">
          <SummaryCard
            label="Final Score"
            value={formatScore(result.finalScore)}
          />
          <SummaryCard label="Final Decision" value={result.finalDecision} />
          <SummaryCard
            label="Hard Rule Breaches"
            value={result.hardRuleBreaches}
          />
          <SummaryCard
            label="Behaviour Score"
            value={formatScore(result.behaviourScore)}
          />
        </div>

        <section className="manage-cases-credit-section">
          <h3>Decision Risk Flags</h3>
          <div className="manage-cases-credit-flags">
            {result.hardRules.map((rule) => (
              <div
                className={`manage-cases-credit-flag ${
                  rule.triggered ? "is-triggered" : ""
                }`}
                key={rule.rule}
              >
                <div>
                  <strong>{rule.rule}</strong>
                  <span>
                    {rule.owner} - {rule.action}
                  </span>
                </div>
                <span>{rule.triggered ? "Triggered" : "Clear"}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="manage-cases-credit-section">
          <h3>Conditional Flags</h3>
          <div className="manage-cases-credit-flags">
            {result.conditionalFlags.map((rule) => (
              <div
                className={`manage-cases-credit-flag ${
                  rule.triggered ? "is-watch" : ""
                }`}
                key={rule.rule}
              >
                <strong>{rule.rule}</strong>
                <span>{rule.triggered ? "Review" : "Clear"}</span>
              </div>
            ))}
          </div>
        </section>
      </aside>
    </div>

    <div className="manage-cases-credit-details">
      <section className="manage-cases-credit-section">
        <h3>Category Summary</h3>
        <div className="manage-cases-credit-table-wrap">
          <table className="manage-cases-credit-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Weight</th>
                <th>Weighted Contribution</th>
              </tr>
            </thead>
            <tbody>
              {result.categorySummary.map((row) => (
                <tr key={row.category}>
                  <td>{row.category}</td>
                  <td>{Math.round(row.weight * 100)}%</td>
                  <td>{formatScore(row.weighted)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="manage-cases-credit-section">
        <h3>Scoring Details</h3>
        <div className="manage-cases-credit-table-wrap">
          <table className="manage-cases-credit-table">
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Weight</th>
                <th>Input</th>
                <th>Score</th>
                <th>Weighted</th>
                <th>Category</th>
                <th>Remark</th>
              </tr>
            </thead>
            <tbody>
              {result.parameters.map((row) => (
                <tr key={`${row.category}-${row.parameter}`}>
                  <td>{row.parameter}</td>
                  <td>{Math.round(row.weight * 1000) / 10}%</td>
                  <td>{row.input}</td>
                  <td>{formatScore(row.score)}</td>
                  <td>{formatScore(row.weighted)}</td>
                  <td>{row.category}</td>
                  <td>{row.remark}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </Modal>
);

const EditableMasterRowsTable = ({
  rows,
  updateMasterRow,
  updateFunderObligation,
}) => (
  <>
    <div
      className="manage-cases-panel-header"
      style={{ padding: "0 0 12px", border: 0 }}
    >
      <div>
        <h2>Master List Preview</h2>
        <span>
          Installment and master-list values can be manually overridden.
        </span>
      </div>
    </div>
    <div className="manage-cases-table-wrap">
      <table className="manage-cases-table">
        <thead>
          <tr>
            <th>Month</th>
            <th>Installment date</th>
            <th>Installment amount</th>
            <th>Status</th>
            <th>Paid date</th>
            <th>Loan end date</th>
            <th>Principal collected</th>
            <th>Interest collected</th>
            <th>Fee collected</th>
            <th>Loan balance</th>
            <th>Funder dues</th>
            <th>BFC revenue</th>
            <th>Principal repaid</th>
            <th>Principal still owing</th>
            <th>Principal repaid to funder</th>
            <th>Loan closed</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.period || index}>
              <td>{row.period}</td>
              <td>
                <input
                  className="manage-cases-table-input"
                  type="date"
                  value={row.due_date || ""}
                  onChange={(event) =>
                    updateMasterRow(index, "due_date", event.target.value)
                  }
                />
              </td>
              <td>
                <input
                  className="manage-cases-table-input is-money"
                  type="number"
                  step="0.01"
                  value={row.amount ?? ""}
                  onChange={(event) =>
                    updateMasterRow(index, "amount", event.target.value)
                  }
                />
              </td>
              <td>
                <select
                  className="manage-cases-table-select"
                  value={row.installment_status || ""}
                  onChange={(event) =>
                    updateMasterRow(
                      index,
                      "installment_status",
                      event.target.value,
                    )
                  }
                >
                  <option value="">Select</option>
                  {INSTALLMENT_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  className="manage-cases-table-input"
                  type="date"
                  value={row.installment_paid_date || ""}
                  onChange={(event) =>
                    updateMasterRow(
                      index,
                      "installment_paid_date",
                      event.target.value,
                    )
                  }
                />
              </td>
              <td>
                <input
                  className="manage-cases-table-input"
                  type="date"
                  value={row.loan_end_date || ""}
                  onChange={(event) =>
                    updateMasterRow(index, "loan_end_date", event.target.value)
                  }
                />
              </td>
              {[
                "principal_collected",
                "interest_collected",
                "fee_collected",
                "loan_balance",
              ].map((field) => (
                <td key={field}>
                  <input
                    className="manage-cases-table-input is-money"
                    type="number"
                    step="0.01"
                    value={row[field] ?? ""}
                    onChange={(event) =>
                      updateMasterRow(index, field, event.target.value)
                    }
                  />
                </td>
              ))}
              <td>
                <FunderObligationsEditor
                  obligations={row.funder_obligations || []}
                  rowIndex={index}
                  updateFunderObligation={updateFunderObligation}
                />
              </td>
              {["bfc_revenue", "principal_repaid", "principal_still_owing"].map(
                (field) => (
                  <td key={field}>
                    <input
                      className="manage-cases-table-input is-money"
                      type="number"
                      step="0.01"
                      value={row[field] ?? ""}
                      onChange={(event) =>
                        updateMasterRow(index, field, event.target.value)
                      }
                    />
                  </td>
                ),
              )}
              <td>
                <select
                  className="manage-cases-table-select"
                  value={yesNoValue(row.principal_repaid_to_funder)}
                  onChange={(event) =>
                    updateMasterRow(
                      index,
                      "principal_repaid_to_funder",
                      event.target.value,
                    )
                  }
                >
                  {YES_NO_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {formatYesNo(option)}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <select
                  className="manage-cases-table-select"
                  value={yesNoValue(row.loan_closed)}
                  onChange={(event) =>
                    updateMasterRow(index, "loan_closed", event.target.value)
                  }
                >
                  {YES_NO_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {formatYesNo(option)}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </>
);

const FunderObligationsEditor = ({
  obligations,
  rowIndex,
  updateFunderObligation,
}) => (
  <div style={{ display: "grid", gap: 10, minWidth: 360 }}>
    {obligations.length === 0 ? (
      <div className="manage-cases-table-small">No funder selected.</div>
    ) : (
      obligations.map((funder, funderIndex) => (
        <div
          key={`${funder.funder_id}-${funderIndex}`}
          className="manage-cases-exposure-card"
        >
          <h3>{funder.funder_name}</h3>
          <div className="manage-cases-row">
            <div className="manage-cases-field">
              <label>Interest due</label>
              <input
                className="manage-cases-table-input is-money"
                type="number"
                step="0.01"
                value={funder.interest_due_to_funder ?? ""}
                onChange={(event) =>
                  updateFunderObligation(
                    rowIndex,
                    funderIndex,
                    "interest_due_to_funder",
                    event.target.value,
                  )
                }
              />
            </div>
            <div className="manage-cases-field">
              <label>Principal due</label>
              <input
                className="manage-cases-table-input is-money"
                type="number"
                step="0.01"
                value={funder.principal_due_to_funder ?? ""}
                onChange={(event) =>
                  updateFunderObligation(
                    rowIndex,
                    funderIndex,
                    "principal_due_to_funder",
                    event.target.value,
                  )
                }
              />
            </div>
          </div>
          <div className="manage-cases-row">
            <div className="manage-cases-field">
              <label>Repayment date</label>
              <input
                className="manage-cases-table-input"
                type="date"
                value={funder.funder_repayment_date || ""}
                onChange={(event) =>
                  updateFunderObligation(
                    rowIndex,
                    funderIndex,
                    "funder_repayment_date",
                    event.target.value,
                  )
                }
              />
            </div>
            <div className="manage-cases-field">
              <label>Status</label>
              <select
                className="manage-cases-table-select"
                value={funder.funder_payment_status || "not due yet"}
                onChange={(event) =>
                  updateFunderObligation(
                    rowIndex,
                    funderIndex,
                    "funder_payment_status",
                    event.target.value,
                  )
                }
              >
                {FUNDER_PAYMENT_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      ))
    )}
  </div>
);

const groupedMasterRows = (rows) => {
  const groupsByCaseId = rows.reduce((groups, row) => {
    const key = row.case_id || `${row.borrower_name}-${row.loan_ref}`;
    const current = groups.get(key) || {
      caseId: key,
      isClosed: false,
      rows: [],
      updatedAt: row.updated_at || "",
    };

    current.isClosed =
      current.isClosed || yesNoValue(row.loan_closed) === "yes";
    current.rows.push(row);
    groups.set(key, current);
    return groups;
  }, new Map());

  return Array.from(groupsByCaseId.values())
    .map((group) => ({
      ...group,
      rows: group.rows.sort(
        (a, b) => numberValue(a.period) - numberValue(b.period),
      ),
    }))
    .sort((a, b) => {
      if (a.isClosed !== b.isClosed) return a.isClosed ? 1 : -1;
      return textValue(a.rows[0]?.borrower_name).localeCompare(
        textValue(b.rows[0]?.borrower_name),
      );
    });
};

const MasterListTable = ({ rows, cases, onEditCase, onDeleteCase }) => {
  const groups = groupedMasterRows(rows);

  return (
    <div className="manage-cases-table-wrap">
      <table className="manage-cases-table">
        <thead>
          <tr>
            <th>Borrower</th>
            <th>Loan ref</th>
            <th>Credit grade</th>
            <th>Approved amount</th>
            <th>Funder exposure</th>
            <th>Tenor</th>
            <th>Loan type</th>
            <th>Loan terms</th>
            <th>Interest / month</th>
            <th>Processing fee</th>
            <th>Disbursed date</th>
            <th>Month</th>
            <th>Installment date</th>
            <th>Installment amount</th>
            <th>Status</th>
            <th>Paid date</th>
            <th>Loan end date</th>
            <th>Principal collected</th>
            <th>Interest collected</th>
            <th>Fee collected</th>
            <th>Loan balance</th>
            <th>Funder dues</th>
            <th>BFC revenue</th>
            <th>Principal repaid</th>
            <th>Principal still owing</th>
            <th>Principal repaid to funder</th>
            <th>Loan closed</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {groups.flatMap((group) =>
            group.rows.map((row, index) => {
              const caseItem = cases.find((item) => item.id === row.case_id);
              const funderExposures = formatFunderExposures(
                row.funder_exposures,
                row.funder_obligations,
              );

              return (
                <tr
                  className={`${group.isClosed ? "is-loan-closed" : ""} ${
                    index === 0 ? "is-loan-group-start" : ""
                  }`}
                  key={`${row.case_id}-${row.period}-${index}`}
                >
                  <td>
                    <strong>{row.borrower_name}</strong>
                    <div className="manage-cases-table-small">
                      {row.borrower_uen || "-"}
                    </div>
                  </td>
                  <td>{row.loan_ref || "-"}</td>
                  <td>{row.credit_grade || "-"}</td>
                  <td>{money(row.approved_amount)}</td>
                  <td>
                    {funderExposures.length === 0
                      ? "-"
                      : funderExposures.map((exposure, exposureIndex) => (
                          <div
                            className="manage-cases-table-small"
                            key={`${exposure.name}-${exposureIndex}`}
                          >
                            <strong>{exposure.name}</strong>
                            <br />
                            {money(exposure.amount)}
                          </div>
                        ))}
                  </td>
                  <td>{formatTenor(row.tenor_months, row.tenor_days)}</td>
                  <td>{row.loan_type || "-"}</td>
                  <td>{row.loan_terms || "-"}</td>
                  <td>{formatPercent(row.interest_per_month)}</td>
                  <td>{formatPercent(row.processing_fee_percentage)}</td>
                  <td>{formatDate(row.disbursed_date)}</td>
                  <td>{row.period}</td>
                  <td>{formatDate(row.due_date)}</td>
                  <td>
                    {row.amount === "" ||
                    row.amount === null ||
                    row.amount === undefined
                      ? "-"
                      : money(row.amount)}
                  </td>
                  <td>{row.installment_status || "-"}</td>
                  <td>{formatDate(row.installment_paid_date)}</td>
                  <td>{formatDate(row.loan_end_date)}</td>
                  <td>{money(row.principal_collected)}</td>
                  <td>{money(row.interest_collected)}</td>
                  <td>{money(row.fee_collected)}</td>
                  <td>{money(row.loan_balance)}</td>
                  <td>
                    {(row.funder_obligations || []).map((funder) => (
                      <div
                        className="manage-cases-table-small"
                        key={funder.funder_id}
                      >
                        <strong>{funder.funder_name}</strong>
                        <br />
                        Interest {money(funder.interest_due_to_funder)} /
                        Principal {money(funder.principal_due_to_funder)}
                        <br />
                        Repay {formatDate(funder.funder_repayment_date)} -{" "}
                        {funder.funder_payment_status || "not due yet"}
                      </div>
                    ))}
                  </td>
                  <td>{money(row.bfc_revenue)}</td>
                  <td>{money(row.principal_repaid)}</td>
                  <td>{money(row.principal_still_owing)}</td>
                  <td>{formatYesNo(row.principal_repaid_to_funder)}</td>
                  <td>{formatYesNo(row.loan_closed)}</td>
                  <td>
                    <Space>
                      <Button
                        size="small"
                        onClick={() => onEditCase(row.case_id)}
                      >
                        Edit
                      </Button>
                      {caseItem ? (
                        <Button
                          danger
                          size="small"
                          onClick={() => onDeleteCase(caseItem)}
                        >
                          Delete
                        </Button>
                      ) : null}
                    </Space>
                  </td>
                </tr>
              );
            }),
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ManageCases;
